'use client'

import { Button } from "@/components/elements/Button"
import Input from "@/components/elements/Input"
import { ChevronDown, ChevronLeft, CircleCheck, HandCoins, Info, MapPinHouse, MapPinPlus, MoveRight, Package, PackageX, ShoppingBag, Store, Truck, Wallet } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { usePathname, useRouter } from "next/navigation"
import { navigateUrl } from "@/utils/client-request"
import { useSession } from "next-auth/react"
import { formatImageURL, formatPrice } from "@/utils/util"
import { toast } from "@/lib/toast"
import Select from "@/components/elements/Select"
import Modal from "@/components/elements/Modal"
import Skeleton from "@/components/elements/Skeleton"
import ProcessingState from "@/components/elements/ProcessingState"
import { useCart, useCustomFields, useAddress, usePayment, useDelivery, useCustomerDetails, useOrderTotal, useCheckoutSubmit } from "@/lib/commerce/hooks"
import CheckoutCard from "./CheckoutCard"

import Image from "next/image"
import Qpay from "@/assets/qpay.svg"
import TransferIcon from "@/assets/transfer.png"
import Storepay from "@/assets/storepay.png"
import Pocket from "@/assets/pocket.png"
import Lend from "@/assets/lendmn.png"
import Booklo from "@/assets/booklo.jpg"
import EmptyState from "../elements/EmptyState"
import TermsDrawer from "../elements/TermsDrawer"
import Textarea from "@/components/elements/Textarea"
import Checkbox from "@/components/elements/Checkbox"
import { Label } from "@/components/ui/label"
import DatePicker from "@/components/elements/DatePicker"
import ImagePicker from "@/components/elements/ImagePicker"
import DiscountTabs from "./DiscountTabs"
import { BuyXGetYOffers, OrderRewardOffers } from "./RewardOffers"
import { UnderlineTabs } from "../elements/Field"
import { DateTimePicker } from "@/components/elements/DateTimePicker"
import ProgressNote from "../elements/ProgressNote"

const ADDRESS_TYPE_LABELS: Record<string, string> = { apartment: 'Орон сууц', house: 'Хашаа байшин', office: 'Оффис' }

export default function Checkout({ vendor, address, branches, customerAddress, subscription }: { vendor: any, address: any, branches: any, customerAddress: any, subscription: any }) {
  const pathname = usePathname()
  const router = useRouter()
  const { data: session } = useSession()
  const { cart, loading, initialLoading, checkout, appliedGiftCard, giftCardDiscount, canGuestOrder, isGuest, appliedCoupon, couponDiscount, isValidating, validateCart, canCheckout, amount, sale, salePriceSavings, discountSavings, discountBreakdown, orderDiscountAmount, hasFreeShipping, freeShippingProgress, allProducts, rewardItems, orderRewardItems, activeOrderDiscount, loyaltySaleOffsetAmount, appliedLoyaltyVoucher } = useCart()
  // Loyalty ni sale-iig orlodog tul haruulahdaa sale-iin morniig loyalty-d
  // orlogdson duugeer horogduulj, loyalty-g undsen uneess buten haruulna.
  const displaySaleSavings = Math.max(0, salePriceSavings - loyaltySaleOffsetAmount)

  const addressApi = useAddress({ address, vendor, customerAddress, isLoggedIn: !!session?.user?.id })
  const {
    addressType, setAddressType,
    selectedAddress, setSelectedAddress,
    addressObj, setAddressObj,
    city, setCity,
    duureg, setDuureg,
    khoroo, setKhoroo,
    bair, setBair,
    toot, setToot,
    additionalAddress, setAdditionalAddress,
    houseNum, setHouseNum,
    street, setStreet,
    building, setBuilding,
    floor, setFloor,
    officeName, setOfficeName,
    selectedProvince, setSelectedProvince,
    selectedDistrict, setSelectedDistrict,
    selectedSubdistrict, setSelectedSubdistrict,
    provinces, districts, subdistricts,
    isAddressDeliverable,
    composeAddressObj,
    validateNewAddress,
    saveNewAddress,
    getCheckoutAddressObj,
  } = addressApi

  const [openAddNewAddress, setOpenAddNewAddress] = useState(false)
  const [newAddressLoading, setNewAddressLoading] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [showOrderDetails, setShowOrderDetails] = useState(false)

  const products = cart?.products || []
  const deliveryApi = useDelivery({ vendor, branches })
  const { selectedDelivery, selectedBranch, setSelectedBranch, selectDelivery, hasDigitalProduct, hasDeliveryOption, hasPickupOption, showDeliverySection } = deliveryApi
  const { subPrice, validGiftCardDiscount, validCouponDiscount, loyaltyDiscount, validLoyaltyVoucherDiscount, deliveryPrice, totalPrice, isFreeOrder, depositAmount, balanceAmount, hasDeposit, payNowAmount } = useOrderTotal({ vendor, selectedDelivery })
  const paymentApi = usePayment({ vendor, products, isFreeOrder, hasDeposit })
  // 'Барааг авахдаа төлөх' means nothing is due now, so it supersedes the deposit.
  const effectiveDeposit = paymentApi.selectedPayment === 'onDelivery' ? 0 : depositAmount
  const { selectedPayment, storepayPhone, setStorepayPhone, lendPhone, setLendPhone, isMethodAllowedForCart, canPayOnDelivery, selectPayment } = paymentApi
  const customFieldsApi = useCustomFields()
  const {
    productValues: customFieldValues,
    orderValues: orderCustomFieldValues,
    setProductField,
    setOrderField,
    uploadProductFile,
    uploadOrderFile,
    isUploadingProductField,
    isUploadingOrderField,
  } = customFieldsApi
  const uploadingFiles = useMemo(() => ({ has: isUploadingProductField }), [isUploadingProductField])
  const uploadingOrderFiles = useMemo(() => ({ has: isUploadingOrderField }), [isUploadingOrderField])
  const handleCustomFieldChange = (fieldKey: string, value: any, _productId?: string) => setProductField(fieldKey, value)
  const handleOrderCustomFieldChange = (fieldKey: string, value: any) => setOrderField(fieldKey, value)
  const handleCustomFileChange = (fieldKey: string, file: File | undefined, _productId?: string) => uploadProductFile(fieldKey, file)
  const handleOrderCustomFileChange = (fieldKey: string, file: File | undefined) => uploadOrderFile(fieldKey, file)

  useEffect(() => {
    if (products.length > 0 && vendor) {
      validateCart()
    }
  }, [products.length, vendor])

  const customerDetailsApi = useCustomerDetails()
  const { fullName, setFullName, email, setEmail, phone, setPhone, additionalPhone, setAdditionalPhone } = customerDetailsApi
  const [termsDrawerOpen, setTermsDrawerOpen] = useState(false)
  const [selectedTermsType, setSelectedTermsType] = useState<'delivery' | 'payment' | 'service' | null>(null)
  const [isTermsAccepted, setIsTermsAccepted] = useState(true)

  const { submit: submitCheckout, isPlacingOrder } = useCheckoutSubmit({
    vendor,
    subscription,
    customerDetails: customerDetailsApi,
    customFields: customFieldsApi,
    address: addressApi,
    payment: paymentApi,
    delivery: deliveryApi,
    totalPrice,
    payNowAmount,
    isFreeOrder,
    hasDigitalProduct,
    hasDeliveryOption,
    showDeliverySection,
  })

  const handleFullNameChange = (event: any) => setFullName(event.target.value)
  const handleEmailChange = (event: any) => setEmail(event.target.value)
  const handlePhoneChange = (event: any) => setPhone(event.target.value)
  const handleAdditionalPhoneChange = (event: any) => setAdditionalPhone(event.target.value)
  const handleBairChange = (event: any) => setBair(event.target.value)
  const handleTootChange = (event: any) => setToot(event.target.value)
  const handleAdditionalAddressChange = (event: any) => setAdditionalAddress(event.target.value)
  const handleHouseNumChange = (event: any) => setHouseNum(event.target.value)
  const handleStreetChange = (event: any) => setStreet(event.target.value)
  const handleBuildingChange = (event: any) => setBuilding(event.target.value)
  const handleFloorChange = (event: any) => setFloor(event.target.value)
  const handleOfficeNameChange = (event: any) => setOfficeName(event.target.value)

  const handlePaymentSelect = selectPayment
  const handleDeliverySelect = selectDelivery

  const handleTermsClick = (type: 'delivery' | 'payment' | 'service') => {
    setSelectedTermsType(type)
    setTermsDrawerOpen(true)
  }

  const addNewAddress = async () => {
    setNewAddressLoading(true)
    const result = await saveNewAddress()
    setNewAddressLoading(false)
    if (!result.ok) return
    setOpenAddNewAddress(false)
    router.refresh()
  }

  const handleCheckout = submitCheckout

  const backgroundColorClass = 'bg-foreground text-background'
  const backgroundColorStyle = {}
  const mutedBackgroundColorClass = 'bg-foreground/5'
  const mutedBackgroundColorStyle = {}
  const textColorClass = 'text-foreground'
  const textColorStyle = {}
  const borderColorClass = 'border-foreground'
  const borderColorStyle = {}
  const getBorderHoverClasses = () => 'border-border hover:border-foreground transition-colors'
  const getBorderHoverStyles = () => ({})

  return (
    <>
      <TermsDrawer isOpen={termsDrawerOpen} onClose={() => setTermsDrawerOpen(false)} type={selectedTermsType} terms={vendor?.terms || {}} vendor={vendor} />
      { /* Mobile Checkout Form */ }
      <div className="block sm:hidden bg-background mb-10 [&_input]:rounded-none [&_textarea]:rounded-none [&_[data-slot=select-trigger]]:rounded-none">
        {initialLoading ? (
          <div className="bg-white px-4 py-5 space-y-4">
            <div className="flex items-center justify-center">
              <Skeleton className="h-5 w-40" />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-11 w-full rounded-none" />
                </div>
              ))}
            </div>
            <div className="space-y-3 pt-2">
              <Skeleton className="h-6 w-28" />
              <div className="grid grid-cols-2 gap-3">
                <Skeleton className="h-16 w-full rounded-none" />
                <Skeleton className="h-16 w-full rounded-none" />
              </div>
            </div>
            <div className="space-y-3 pt-2">
              <Skeleton className="h-6 w-32" />
              <div className="grid grid-cols-2 gap-3">
                <Skeleton className="h-14 w-full rounded-none" />
                <Skeleton className="h-14 w-full rounded-none" />
              </div>
            </div>
            <div className="space-y-3 pt-4">
              <Skeleton className="h-5 w-36" />
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Skeleton className="w-16 h-16 rounded-none shrink-0" />
                  <div className="flex flex-col w-full gap-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
              <div className="space-y-2 pt-3">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex justify-between pt-2 border-t border-border border-dashed">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24" />
                </div>
              </div>
              <Skeleton className="h-12 w-full rounded-none" />
            </div>
          </div>
        ) : products.length === 0 ? (
          isPlacingOrder ? (
            <div className="bg-white px-4 py-6">
              <ProcessingState />
            </div>
          ) : (
            <div className="bg-white px-4 py-12 flex flex-col items-center justify-center">
              <EmptyState icon={ShoppingBag} title="Таны сагс хоосон байна" description="Та яг одоо сагсандаа бараа нэмээрэй!"><a href={navigateUrl('/product', pathname)} className="inline-flex items-center h-11 px-8 border border-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background">Дэлгүүр хэсэх</a></EmptyState>
            </div>
          )
        ) : (
          <>
            <div className="bg-white">
              {vendor?.isDanVerified === false && (
                <div className="px-4">
                  <div className="flex gap-3 col-span-full p-3 bg-amber-50 border border-amber-100 rounded-none text-warning">
                    <Info size={14} className="shrink-0 mt-1" />
                    <div className="text-xs font-medium leading-5">
                      <span className="font-medium">{vendor?.name}</span> дэлгүүр нь <span className="font-medium">Улсын бүртгэлээр баталгаажаагүй</span> тул дэлгүүрийн үйлчилгээний нөхцөлтэй сайтар танилцсаны дараа эрсдэлийг тооцож худалдан авалт хийхийг зөвлөж байна.
                    </div>
                  </div>
                </div>
              )}

              <div className="px-4 py-5 flex items-center justify-center gap-2" onClick={() => setShowOrderDetails(!showOrderDetails)}>
                <div className="text-foreground font-medium leading-5 tracking-[.0125em]">
              Захиалгын дэлгэрэнгүй
                </div>
                <ChevronDown className={`w-5 h-5 text-foreground transition-transform duration-200 ${showOrderDetails ? 'rotate-180' : ''}`} />
              </div>
              {showOrderDetails && (
                <div className="px-4 pb-4">
                  <div className="space-y-4">
                    {initialLoading ? (
                      <div className="flex flex-col gap-3">
                        {Array.from({ length: 3 }).map((_, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <Skeleton className="w-16 h-16 rounded-none shrink-0" />
                            <div className="flex flex-col w-full space-y-2">
                              <div className="flex items-center justify-between">
                                <Skeleton className="w-32 h-4" />
                                <Skeleton className="w-16 h-4" />
                              </div>
                              <Skeleton className="w-24 h-3" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        {allProducts.map((product: any, index: number) => (
                          <CheckoutCard key={index} product={product} />
                        ))}
                      </>
                    )}
                  </div>

                  <BuyXGetYOffers className="mt-4" />

                  <OrderRewardOffers className="mt-4" />
                </div>
              )}
            </div>

            <div className="bg-white">
              <div className="flex flex-col gap-y-3 py-3 px-4 mt-3">
                <Input label="Нэр" placeholder="Таны нэр" value={fullName} onChange={handleFullNameChange} autoFocus color="zinc-800" />
                <Input label="Имэйл хаяг" type="email" placeholder="example@shop.mn" value={email} onChange={handleEmailChange} disabled={!!session?.user?.email} color="zinc-800" required={hasDigitalProduct} description={hasDigitalProduct ? "Дижитал бараа авч байгаа тул имэйл хаяг заавал оруулна уу." : session?.user?.email ? undefined : "Имэйл хаягийг заавал бөглөх шаардлагагүй ба хэрэв бөглөсөн тохиолдолд та захиалгын мэдээллээ имэйлээр хүлээн авах боломжтой."} />
                <Input label="Утасны дугаар" type="tel" inputMode="numeric" placeholder="00000000" value={phone} onChange={handlePhoneChange} required color="zinc-800" />
                <Input label="Нэмэлт утасны дугаар" type="tel" inputMode="numeric" placeholder="00000000" value={additionalPhone} onChange={handleAdditionalPhoneChange} color="zinc-800" />
              </div>
            </div>

            {subscription?.canUseCustomFields && products.some((product: any) => product.fields?.length > 0) && (
              <div className="bg-white mt-3">
                <div className="py-3 px-4">
                  <div className="text-lg font-medium leading-7 text-foreground tracking-[.0125em] mb-2">Бүтээгдэхүүний нэмэлт мэдээлэл</div>
                  <div className="space-y-4">
                    {products.filter((product: any) => product.fields?.length > 0).map((product: any, productIndex: number) => {
                      const productFields = product.fields || []
                      return (
                        <div key={productIndex} className="col-span-full border border-border rounded-none overflow-hidden p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-none overflow-hidden shrink-0 relative">
                              {product.variant?.images?.[0] ? (
                                <img src={formatImageURL(product.variant.images[0])} alt={product.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="flex items-center justify-center w-full h-full bg-muted">
                                  <Package className="w-6 h-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-foreground leading-5 tracking-[.0125em] line-clamp-2">{product.name}</h4>
                              <p className="text-xs text-muted-foreground mt-1">
                            Бүтээгдэхүүнд шаардагдах нэмэлт {productFields.length} мэдээлэл
                              </p>
                            </div>
                          </div>
                          {(
                            <div className="mt-4 space-y-4">
                              {productFields.map((field: any, fieldIndex: number) => {
                                const fieldKey = `${product.id}-${field.name}`
                                const fieldValue = customFieldValues[fieldKey] || field.value || ''

                                if(field.type === 'text' || field.type === 'number') {
                                  return (
                                    <Input key={fieldIndex} label={field.name} type={field.type} placeholder={`${field.name} оруулна уу`} value={fieldValue} onChange={(e) => handleCustomFieldChange(fieldKey, e.target.value, product.id)} required={field.required} color="zinc-800" />
                                  )
                                }

                                if(field.type === 'textarea') {
                                  return (
                                    <Textarea key={fieldIndex} label={field.name} placeholder={`${field.name} оруулна уу`} value={fieldValue} onChange={(e) => handleCustomFieldChange(fieldKey, e.target.value, product.id)} required={field.required} color="zinc-800" />
                                  )
                                }

                                if(field.type === 'select') {
                                  const selectItems = (field.options || []).map((option: string) => ({ label: option, value: option }))
                                  return (
                                    <Select key={fieldIndex} label={field.name} items={selectItems} value={fieldValue} onChange={(value: string) => handleCustomFieldChange(fieldKey, value, product.id)} required={field.required} color="zinc-800" />
                                  )
                                }

                                if(field.type === 'checkbox') {
                                  return (
                                    <Checkbox key={fieldIndex} title={field.name} description="" checked={fieldValue === 'true'} onChange={() => handleCustomFieldChange(fieldKey, fieldValue === 'true' ? 'false' : 'true', product.id)} required={field.required} />
                                  )
                                }

                                if(field.type === 'date') {
                                  const dateValue = fieldValue ? (typeof fieldValue === 'string' ? new Date(fieldValue) : fieldValue) : null
                                  return (
                                    <DatePicker 
                                      key={fieldIndex} 
                                      label={field.name} 
                                      date={dateValue}
                                      setDate={(date: Date | null) => {
                                        handleCustomFieldChange(fieldKey, date ? date.toISOString() : '', product.id)
                                      }}
                                      placeholder={`${field.name} сонгоно уу`}
                                      required={field.required}
                                      className="w-full"
                                    />
                                  )
                                }

                                if(field.type === 'productImage') {
                                  return (
                                    <ImagePicker key={fieldIndex} label={field.name} images={field.options || []} value={fieldValue} multiple={field.multiple} onChange={(image: any) => handleCustomFieldChange(fieldKey, image, product.id)} required={field.required} color="zinc-800" />
                                  )
                                }

                                if(field.type === 'file') {
                                  const fileValue = customFieldValues[fieldKey]
                                  const fileUrl = typeof fileValue === 'string' ? fileValue : fileValue?.url
                                  const isImage = fileUrl && fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                                  const isPdf = fileUrl && fileUrl.match(/\.pdf$/i)
                                  const isUploading = uploadingFiles.has(fieldKey)
                              
                                  return (
                                    <div key={fieldIndex} className="col-span-full">
                                      <Label>{field.name}{field.required && <span className="text-red-600 ml-1">*</span>}</Label>
                                      <div className="mt-2.5 flex items-center gap-x-4">
                                        <div className="relative cursor-pointer group" onClick={() => document.getElementById(`file-${fieldKey}`)?.click()}>
                                          {isUploading ? (
                                            <div className="rounded-none w-16 h-16 border-2 border-border flex items-center justify-center bg-muted">
                                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                            </div>
                                          ) : isImage ? (
                                            <img src={formatImageURL(fileUrl)} alt={field.name} className="rounded-none w-16 h-16 object-cover border-2 border-transparent hover:border-primary transition-colors" />
                                          ) : isPdf ? (
                                            <div className="rounded-none w-16 h-16 border-2 border-border flex items-center justify-center bg-muted hover:border-primary transition-colors">
                                              <Image src={"/pdf.png"} alt="PDF" width={48} height={48} className="w-12 h-12 object-contain" />
                                            </div>
                                          ) : (
                                            <div className="rounded-none w-16 h-16 border-2 border-dashed border-border flex items-center justify-center hover:border-primary transition-colors">
                                              <span className="text-muted-foreground text-xs">Файл</span>
                                            </div>
                                          )}
                                          {fileValue && (
                                            <div className="absolute inset-0 bg-black/40 rounded-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                              <span className="text-white text-xs font-medium">Солих</span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <Button onClick={() => !isUploading && document.getElementById(`file-${fieldKey}`)?.click()} variant="outline" size="sm" type="button" disabled={isUploading}>
                                            {isUploading ? "Хуулж байна..." : fileUrl ? "Солих" : "Файл сонгох"}
                                          </Button>
                                          {fileValue && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                              {typeof fileValue === 'string' ? fileValue.split('/').pop() : fileValue?.originalName}
                                            </div>
                                          )}
                                        </div>
                                        <input 
                                          id={`file-${fieldKey}`}
                                          type="file" 
                                          accept="image/*,application/pdf" 
                                          className="hidden" 
                                          onChange={(e) => {
                                            const file = e.target.files?.[0]
                                            if (file) handleCustomFileChange(fieldKey, file, product.id)
                                          }} 
                                        />
                                      </div>
                                    </div>
                                  )
                                }
                                return null
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {subscription?.canUseCustomFields && vendor?.orderFields?.length > 0 && (
              <div className="bg-white mt-3">
                <div className="py-3 px-4">
                  <div className="text-lg font-medium leading-7 text-foreground tracking-[.0125em] mb-4">Захиалгын нэмэлт мэдээлэл</div>
                  <div className="space-y-4">
                    {vendor.orderFields.map((field: any, fieldIndex: number) => {
                      const fieldKey = field.name
                      const fieldValue = orderCustomFieldValues[fieldKey] || field.value || ''

                      if (field.type === 'text' || field.type === 'number') {
                        return (
                          <Input key={fieldIndex} label={field.name} type={field.type} placeholder={`${field.name} оруулна уу`} value={fieldValue} onChange={(e) => handleOrderCustomFieldChange(fieldKey, e.target.value)} required={field.required} color="zinc-800" />
                        )
                      }

                      if (field.type === 'textarea') {
                        return (
                          <Textarea key={fieldIndex} label={field.name} placeholder={`${field.name} оруулна уу`} value={fieldValue} onChange={(e) => handleOrderCustomFieldChange(fieldKey, e.target.value)} required={field.required} color="zinc-800" />
                        )
                      }

                      if (field.type === 'select') {
                        const selectItems = (field.options || []).map((option: string) => ({ label: option, value: option }))
                        return (
                          <Select key={fieldIndex} label={field.name} items={selectItems} value={fieldValue} onChange={(value: string) => handleOrderCustomFieldChange(fieldKey, value)} required={field.required} color="zinc-800" />
                        )
                      }

                      if (field.type === 'checkbox') {
                        return (
                          <Checkbox key={fieldIndex} title={field.name} description="" checked={fieldValue === 'true'} onChange={() => handleOrderCustomFieldChange(fieldKey, fieldValue === 'true' ? 'false' : 'true')} required={field.required} />
                        )
                      }

                      if (field.type === 'date') {
                        const dateValue = fieldValue ? (typeof fieldValue === 'string' ? new Date(fieldValue) : fieldValue) : null
                        return (
                          <DateTimePicker key={fieldIndex} label={field.name} date={dateValue} setDate={(date: Date | undefined) => { handleOrderCustomFieldChange(fieldKey, date ? date.toISOString() : '') }} placeholder={`${field.name} сонгоно уу`} required={field.required} className="w-full" />
                        )
                      }

                      if (field.type === 'file') {
                        const fileValue = orderCustomFieldValues[fieldKey]
                        const fileUrl = typeof fileValue === 'string' ? fileValue : fileValue?.url
                        const isImage = fileUrl && fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                        const isPdf = fileUrl && fileUrl.match(/\.pdf$/i)
                        const isUploading = uploadingOrderFiles.has(fieldKey)

                        return (
                          <div key={fieldIndex} className="col-span-full">
                            <Label>{field.name}{field.required && <span className="text-red-600 ml-1">*</span>}</Label>
                            <div className="mt-2.5 flex items-center gap-x-4">
                              <div className="relative cursor-pointer group" onClick={() => document.getElementById(`order-file-${fieldKey}`)?.click()}>
                                {isUploading ? (
                                  <div className="rounded-none w-16 h-16 border-2 border-border flex items-center justify-center bg-muted">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                  </div>
                                ) : isImage ? (
                                  <img src={formatImageURL(fileUrl)} alt={field.name} className="rounded-none w-16 h-16 object-cover border-2 border-transparent hover:border-primary transition-colors" />
                                ) : isPdf ? (
                                  <div className="rounded-none w-16 h-16 border-2 border-border flex items-center justify-center bg-muted hover:border-primary transition-colors">
                                    <Image src={"/pdf.png"} alt="PDF" width={48} height={48} className="w-12 h-12 object-contain" />
                                  </div>
                                ) : (
                                  <div className="rounded-none w-16 h-16 border-2 border-dashed border-border flex items-center justify-center hover:border-primary transition-colors">
                                    <span className="text-muted-foreground text-xs">Файл</span>
                                  </div>
                                )}
                                {fileValue && (
                                  <div className="absolute inset-0 bg-black/40 rounded-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <span className="text-white text-xs font-medium">Солих</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                <Button onClick={() => !isUploading && document.getElementById(`order-file-${fieldKey}`)?.click()} variant="outline" size="sm" type="button" disabled={isUploading}>
                                  {isUploading ? "Хуулж байна..." : fileUrl ? "Солих" : "Файл сонгох"}
                                </Button>
                                {fileValue && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {typeof fileValue === 'string' ? fileValue.split('/').pop() : fileValue?.originalName}
                                  </div>
                                )}
                              </div>
                              <input id={`order-file-${fieldKey}`} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleOrderCustomFileChange(fieldKey, file) }} />
                            </div>
                          </div>
                        )
                      }
                      return null
                    })}
                  </div>
                </div>
              </div>
            )}

            {showDeliverySection && (
              <div className="bg-white">
                <div className="py-3 px-4 my-3">
                  <div className="text-lg font-medium leading-7 text-foreground tracking-[.0125em] mb-2">Хүргэлтийн төрөл</div>
                  {!hasDeliveryOption && hasPickupOption && (
                    <div className="py-1 pl-4 border-l border-foreground mb-3">
                      <div className="text-xs text-foreground">
                    Манай дэлгүүр одоогоор хүргэлтийн үйлчилгээ үзүүлэхгүй тул та зөвхөн салбар дээрээс ирж авах боломжтой.
                      </div>
                    </div>
                  )}
                  {selectedDelivery === "delivery" && hasDeliveryOption && vendor?.isBooklo ? (
                    <div className="py-1 pl-4 border-l border-foreground mb-3">
                      <div className="text-xs text-foreground">
                        <strong>Анхаар:</strong> Зөвхөн Booklo хүргэлтийн бүсэд багтах дүүрэг, хороо сонгогдоно.
                      </div>
                    </div>
                  ) : selectedDelivery === "delivery" && hasDeliveryOption && !vendor?.canDeliverCountryside && (
                    <div className="py-1 pl-4 border-l border-foreground mb-3">
                      <div className="text-xs text-foreground">
                        <strong>Анхаар:</strong> Манай дэлгүүр зөвхөн Улаанбаатар хот дотор хүргэлт хийнэ.
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    {hasDeliveryOption && (
                      <div className="space-y-2">
                        <div onClick={() => handleDeliverySelect("delivery")} className={`cursor-pointer rounded-none flex items-center p-3 transition-all duration-200 hover:bg-background ${selectedDelivery === "delivery" ? `${mutedBackgroundColorClass}` : ""}`} style={selectedDelivery === "delivery" ? mutedBackgroundColorStyle : undefined}>
                          <div className="flex items-center w-full">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 transition-all duration-200 ${selectedDelivery === "delivery" ? `border-current ${textColorClass}` : "border-border"}`} style={selectedDelivery === "delivery" ? textColorStyle : undefined}>
                              {selectedDelivery === "delivery" && (
                                <div className={`w-2.5 h-2.5 rounded-full ${backgroundColorClass}`} style={backgroundColorStyle}></div>
                              )}
                            </div>
                            <div className="flex justify-between items-center w-full">
                              <div className="flex flex-col">
                                <div className="font-medium text-sm text-foreground">
                              Хүргэлтээр авах
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {vendor?.isBooklo ? (
                                    <span className="text-foreground">Booklo хүргэлт 6&apos;500₮</span>
                                  ) : (
                                    <>
                                      {vendor?.deliveryType === 'constantDelivery' && vendor?.deliveryPrice > 0 && (
                                        <span className="text-foreground">{formatPrice(vendor.deliveryPrice)} хүргэлтийн төлбөр</span>
                                      )}
                                      {vendor?.deliveryType === 'freeDelivery' && (
                                        <span className="text-foreground">Үнэгүй хүргэлт</span>
                                      )}
                                      {vendor?.deliveryType === 'minOrderAmount' && vendor?.minOrderAmount > 0 && (
                                        <span className="text-foreground">
                                          {formatPrice(vendor.minOrderAmount)}-аас дээш үнэгүй
                                        </span>
                                      )}
                                      {vendor?.deliveryType === 'laterDelivery' && (
                                        <span className="text-amber-600">Үнэ тусдаа тооцогдоно</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                              {vendor?.isBooklo ? (
                                <Image src={Booklo} alt="Booklo" width={24} height={24} className="w-6 h-6 rounded-none object-contain" />
                              ) : (
                                <Truck className="w-6 h-6 text-foreground" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {hasPickupOption && (
                      <div onClick={() => handleDeliverySelect("pickup")} className={`cursor-pointer rounded-none flex items-center p-3 transition-all duration-200 hover:bg-background ${selectedDelivery === "pickup" ? `${mutedBackgroundColorClass}` : ""}`} style={selectedDelivery === "pickup" ? mutedBackgroundColorStyle : undefined}>
                        <div className="flex items-center w-full">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 transition-all duration-200 ${selectedDelivery === "pickup" ? `border-current ${textColorClass}` : "border-border"}`} style={selectedDelivery === "pickup" ? textColorStyle : undefined}>
                            {selectedDelivery === "pickup" && (
                              <div className={`w-2.5 h-2.5 rounded-full ${backgroundColorClass}`} style={backgroundColorStyle}></div>
                            )}
                          </div>
                          <div className="flex items-center justify-between w-full">
                            <div className="flex flex-col">
                              <div className="font-medium text-sm text-foreground">
                            Дэлгүүрээс очиж авах
                              </div>
                            </div>
                            <Store className="w-6 h-6 text-foreground" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedDelivery === "delivery" && hasDeliveryOption && (
                    <>
                      <div className="text-lg font-medium leading-7 text-foreground tracking-[.0125em] mt-2.5">Хүргэлтийн хаяг</div>
                      {session?.user?.id ? (
                        <div className="flex flex-col gap-3">
                          <Button width="full" icon={MapPinPlus} color="zinc-800" className="mb-1 mt-4" onClick={() => setOpenAddNewAddress(true)}>
                        Хаяг нэмэх
                          </Button>
                          {session?.user?.id && customerAddress?.length > 0 && customerAddress.map((address: any, index: number) => {
                            const isDisabled = (!vendor?.canDeliverCountryside && address.city !== 'Улаанбаатар') || !isAddressDeliverable(address)
                            return (
                              <div
                                key={index}
                                onClick={() => {
                                  if (isDisabled) return
                                  setSelectedAddress(address.id)
                                  setAddressObj({
                                    city: address.city,
                                    duureg: address.duureg,
                                    khoroo: address.khoroo,
                                    bair: address.bair,
                                    toot: address.toot,
                                    additionalAddress: address.additionalAddress,
                                    type: address.type,
                                    houseNum: address.houseNum,
                                    street: address.street,
                                    building: address.building,
                                    floor: address.floor,
                                    officeName: address.officeName
                                  })
                                }}
                                className={`border rounded-none flex flex-col p-4 ${isDisabled ? "cursor-not-allowed opacity-50 bg-foreground/5 border-border" : `cursor-pointer ${selectedAddress === address.id ? `${borderColorClass}` : "border-input"}`}`} style={selectedAddress === address.id ? borderColorStyle : undefined}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex flex-1 justify-between items-center">
                                    <div className="flex flex-col gap-1">
                                      <div className="w-fit"><span className="text-[11px] tracking-[0.14em] uppercase text-muted-foreground">{ADDRESS_TYPE_LABELS[address.type] || ADDRESS_TYPE_LABELS.apartment}</span></div>
                                      <div className={`text-sm font-medium ${isDisabled ? "text-muted-foreground" : ""}`}>
                                        {address.city}, {address.duureg}, {address.khoroo}
                                        {isDisabled && (
                                          <span className="ml-2 inline-flex text-xs text-red-600 bg-red-100 px-2 py-1 rounded-none">
                                        Хүргэлт боломжгүй
                                          </span>
                                        )}
                                      </div>
                                      <div className={`text-xs ${isDisabled ? "text-muted-foreground" : "text-muted-foreground"}`}>
                                        {address.type === 'apartment' && `${address.bair}-р байр, тоот ${address.toot}`}
                                        {address.type === 'house' && `${address.street ? `${address.street}, ` : ''}${address.houseNum}`}
                                        {address.type === 'office' && `${address.building}, ${address.floor}-р давхар, тоот ${address.toot}${address.officeName ? ` (${address.officeName})` : ''}`}
                                        {!address.type && `${address.bair}-р байр, тоот ${address.toot}`}
                                      </div>
                                      <div className={`text-xs ${isDisabled ? "text-muted-foreground" : "text-muted-foreground"}`}>
                                        {address.additionalAddress}
                                      </div>
                                    </div>
                                    <CircleCheck className={`w-5 h-5 ${selectedAddress === address.id && !isDisabled ? `${textColorClass}` : "hidden"}`} style={selectedAddress === address.id && !isDisabled ? textColorStyle : undefined} />
                                  </div>
                                </div>
                              </div>
                            )
                          })}

                          {customerAddress?.length > 0 && customerAddress.every((addr: any) => !isAddressDeliverable(addr)) && (
                            <div className="col-span-full p-3 bg-red-50 border border-red-200 rounded-none">
                              <div className="text-xs text-red-800">
                            Таны хадгалсан хаягууд руу хүргэлт хийх боломжгүй байна. Шинэ хаяг нэмнэ үү.
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="mt-4 flex flex-col gap-y-3">
                          <UnderlineTabs label="Хаягийн төрөл" options={[{ value: 'apartment', label: 'Орон сууц' }, { value: 'house', label: 'Хашаа байшин' }, { value: 'office', label: 'Оффис' }]} value={addressType} onChange={(value: string) => setAddressType(value as 'apartment' | 'house' | 'office')} />
                          <Select label="Хот / Аймаг" items={!vendor?.canDeliverCountryside ? provinces.filter((province: any) => province.value === 'Улаанбаатар') : provinces} value={selectedProvince || ''} onChange={(value: string) => { setSelectedProvince(value); setCity(value); setSelectedDistrict(null); setDuureg(''); setSelectedSubdistrict(null); setKhoroo('') }} required />
                          <Select label="Дүүрэг / Сум" value={selectedDistrict || ''} items={districts} required onChange={(value: string) => { setSelectedDistrict(value); setDuureg(value); setSelectedSubdistrict(null); setKhoroo('') }} />
                          <Select label="Хороо / Баг" value={selectedSubdistrict || ''} items={subdistricts} required onChange={(value: string) => { setSelectedSubdistrict(value); setKhoroo(value) }} />
                          {addressType === 'apartment' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <Input label="Байр" placeholder="Байрны дугаарыг оруулна уу" value={bair} onChange={handleBairChange} required color="zinc-800" />
                              <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={toot} onChange={handleTootChange} required color="zinc-800" />
                            </div>
                          )}
                          {addressType === 'house' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <Input label="Хашааны дугаар" placeholder="Хашааны дугаар оруулна уу" value={houseNum} onChange={handleHouseNumChange} required color="zinc-800" />
                              <Input label="Гудамж" placeholder="Гудамжны нэр" value={street} onChange={handleStreetChange} color="zinc-800" />
                            </div>
                          )}
                          {addressType === 'office' && (
                            <>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input label="Барилга" placeholder="Барилгын нэр/дугаар" value={building} onChange={handleBuildingChange} required color="zinc-800" />
                                <Input label="Давхар" placeholder="Давхар" value={floor} onChange={handleFloorChange} required color="zinc-800" />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={toot} onChange={handleTootChange} required color="zinc-800" />
                                <Input label="Оффисын нэр" placeholder="Оффисын нэр" value={officeName} onChange={handleOfficeNameChange} color="zinc-800" />
                              </div>
                            </>
                          )}
                          <Input label="Нэмэлт мэдээлэл" placeholder="Нэмэлт мэдээлэл оруулна уу" value={additionalAddress} onChange={handleAdditionalAddressChange} description="Орц, давхар, орцны код гэх мэт" color="zinc-800" />
                        </div>
                      )}
                    </>
                  )}

                  {selectedDelivery === "pickup" && vendor?.canPickup && branches?.length > 0 ? (
                    <>
                      <div className="text-lg font-medium leading-7 text-foreground tracking-[.0125em] mt-2.5">Салбарын хаяг</div>
                      <div className="mt-4 flex flex-col gap-y-3">
                        {branches.map((branch: any, index: number) => (
                          <div key={index} onClick={() => setSelectedBranch(branch.id)} className={`cursor-pointer border rounded-none flex flex-col items-center justify-center p-4 ${selectedBranch === branch.id ? `${borderColorClass}` : "border-input"}`} style={selectedBranch === branch.id ? borderColorStyle : undefined}>
                            <div className="flex flex-col items-center mx-auto">
                              <div className="flex items-center gap-2 mt-1.5">
                                {selectedBranch === branch.id ? (
                                  <CircleCheck className={`w-5 h-5 ${textColorClass}`} style={textColorStyle} />
                                ) : (
                                  <Store className={`w-5 h-5 text-foreground`} />
                                )}
                                <span className="text-sm font-medium">{branch.name}</span>
                                {branch.isDefault && (
                                  <span className={`text-xs text-white ${backgroundColorClass} px-2 py-0.5 rounded-full`} style={backgroundColorStyle}>Үндсэн</span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground mt-1.5">{branch.address}</span>
                              <span className="text-xs text-muted-foreground mt-1">Утас: {branch.phone}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : selectedDelivery === "pickup" && !vendor?.canPickup ? (
                    <div className="col-span-full py-1 pl-4 border-l border-foreground">
                      <div className="text-xs text-foreground">
                        <strong>Анхаар:</strong> Салбараас авах үйлчилгээ одоогоор хаагдсан байна.
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {!isFreeOrder && (
              <div className="bg-white">
                <div className="flex flex-col gap-y-3 py-3 px-4 my-3">
                  <div className="text-lg font-medium leading-7 text-foreground tracking-[.0125em]">Төлбөрийн сонголт</div>
                  {/* Render order mirrors the auto-select precedence in usePayment,
                      so the pre-selected method is always the first row. */}
                  <div className="space-y-2">
                    {canPayOnDelivery && isMethodAllowedForCart('onDelivery') && (
                      <div onClick={() => handlePaymentSelect("onDelivery")} className={`cursor-pointer rounded-none flex items-center p-4 transition-all duration-200 hover:bg-background ${selectedPayment === "onDelivery" ? `${mutedBackgroundColorClass}` : ""}`} style={selectedPayment === "onDelivery" ? mutedBackgroundColorStyle : undefined}>
                        <div className="flex items-center w-full">
                          <div className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center mr-4 transition-all duration-200 ${selectedPayment === "onDelivery" ? `border-current ${textColorClass}` : "border-border"}`} style={selectedPayment === "onDelivery" ? textColorStyle : undefined}>
                            {selectedPayment === "onDelivery" && (
                              <div className={`w-2.5 h-2.5 rounded-full ${backgroundColorClass}`} style={backgroundColorStyle}></div>
                            )}
                          </div>
                          <div className="flex justify-between items-center w-full gap-3">
                            <div className="flex flex-col gap-0.5">
                              <div className="text-sm font-medium text-foreground">Барааг авахдаа төлөх</div>
                              <div className="text-muted-foreground text-xs">Барааг хүлээн авахдаа шууд төлнө</div>
                            </div>
                            <HandCoins className="w-6 h-6 shrink-0 text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    )}

                    {vendor?.qpayMerchant && subscription?.canUseQPay && vendor?.canQpay !== false && isMethodAllowedForCart('qpay') && (
                      <div onClick={() => handlePaymentSelect("qpay")} className={`cursor-pointer rounded-none flex items-center p-4 transition-all duration-200 hover:bg-background ${selectedPayment === "qpay" ? `${mutedBackgroundColorClass}` : ""}`} style={selectedPayment === "qpay" ? mutedBackgroundColorStyle : undefined}>
                        <div className="flex items-center w-full">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 transition-all duration-200 ${selectedPayment === "qpay" ? `border-current ${textColorClass}` : "border-border"}`} style={selectedPayment === "qpay" ? textColorStyle : undefined}>
                            {selectedPayment === "qpay" && (
                              <div className={`w-2.5 h-2.5 rounded-full ${backgroundColorClass}`} style={backgroundColorStyle}></div>
                            )}
                          </div>
                          <div className="flex justify-between items-center w-full">
                            <div className="text-sm font-medium text-foreground">
                        QPay
                            </div>
                            <Image src={Qpay} alt="QPay" height={24} width={24} className="w-6 h-6 object-cover" />
                          </div>

                        </div>
                      </div>
                    )}

                    {vendor?.canTransfer && vendor?.canSimple !== false && isMethodAllowedForCart('transfer') && (
                      <div onClick={() => handlePaymentSelect("transfer")} className={`cursor-pointer rounded-none flex items-center p-4 transition-all duration-200 hover:bg-background ${selectedPayment === "transfer" ? `${mutedBackgroundColorClass}` : ""}`} style={selectedPayment === "transfer" ? mutedBackgroundColorStyle : undefined}>
                        <div className="flex items-center w-full">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 transition-all duration-200 ${selectedPayment === "transfer" ? `border-current ${textColorClass}` : "border-border"}`} style={selectedPayment === "transfer" ? textColorStyle : undefined}>
                            {selectedPayment === "transfer" && (
                              <div className={`w-2.5 h-2.5 rounded-full ${backgroundColorClass}`} style={backgroundColorStyle}></div>
                            )}
                          </div>
                          <div className="flex justify-between items-center w-full">
                            <div className="text-sm font-medium text-foreground">
                        Дансаар шилжүүлэх
                            </div>
                            <Image src={TransferIcon} alt="Transfer" height={24} width={24} className="w-6 h-6 object-cover" />
                          </div>
                        </div>
                      </div>
                    )}

                    {vendor?.storepayCredentials && vendor?.canStorepay !== false && isMethodAllowedForCart('storepay') && (
                      <div onClick={() => totalPrice >= 100000 ? handlePaymentSelect("storepay") : null} className={`rounded-none flex flex-col p-4 transition-all duration-200 ${totalPrice >= 100000 ? `cursor-pointer hover:bg-background ${selectedPayment === "storepay" ? `${mutedBackgroundColorClass}` : ""}` : "opacity-50 cursor-not-allowed"}`} style={selectedPayment === "storepay" && totalPrice >= 100000 ? mutedBackgroundColorStyle : undefined}>
                        <div className="flex items-center w-full">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 transition-all duration-200 ${selectedPayment === "storepay" && totalPrice >= 100000 ? `border-current ${textColorClass}` : "border-border"}`} style={selectedPayment === "storepay" && totalPrice >= 100000 ? textColorStyle : undefined}>
                            {selectedPayment === "storepay" && totalPrice >= 100000 && (
                              <div className={`w-2.5 h-2.5 rounded-full ${backgroundColorClass}`} style={backgroundColorStyle}></div>
                            )}
                          </div>
                          <div className="flex justify-between items-center w-full">
                            <div className="flex flex-col gap-0.5 w-full">
                              <div className="text-sm font-medium text-foreground">
                          StorePay
                              </div>
                              {totalPrice <= 100000 && (
                                <div className="text-muted-foreground text-xs">
                            Зөвхөн 100,000₮-с дээш
                                </div>
                              )}
                            </div>
                            <Image src={Storepay} alt="Storepay" height={24} width={24} className="w-6 h-6 object-cover" />
                          </div>
                        </div>
                        {selectedPayment === "storepay" && totalPrice >= 100000 && (
                          <div className="mt-4">
                            <Input value={storepayPhone} onChange={(e) => setStorepayPhone(e.target.value)} placeholder="StorePay бүртгэлтэй утасны дугаар" container="!max-w-none" color="zinc-800" type="text" required />
                          </div>
                        )}
                      </div>
                    )}

                    {vendor?.pocketCredentials && vendor?.canPocket !== false && isMethodAllowedForCart('pocket') && (
                      <div onClick={() => totalPrice >= 100000 ? handlePaymentSelect("pocket") : null} className={`rounded-none flex flex-col p-4 transition-all duration-200 ${totalPrice >= 100000 ? `cursor-pointer hover:bg-background ${selectedPayment === "pocket" ? `${mutedBackgroundColorClass}` : ""}` : "opacity-50 cursor-not-allowed"}`} style={selectedPayment === "pocket" && totalPrice >= 100000 ? mutedBackgroundColorStyle : undefined}>
                        <div className="flex items-center w-full">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 transition-all duration-200 ${selectedPayment === "pocket" && totalPrice >= 100000 ? `border-current ${textColorClass}` : "border-border"}`} style={selectedPayment === "pocket" && totalPrice >= 100000 ? textColorStyle : undefined}>
                            {selectedPayment === "pocket" && totalPrice >= 100000 && (
                              <div className={`w-2.5 h-2.5 rounded-full ${backgroundColorClass}`} style={backgroundColorStyle}></div>
                            )}
                          </div>
                          <div className="flex justify-between items-center w-full">
                            <div className="flex flex-col gap-0.5 w-full">
                              <div className="text-sm font-medium text-foreground">
                          PocketZero
                              </div>
                              {totalPrice <= 100000 && (
                                <div className="text-muted-foreground text-xs">
                            Зөвхөн 100,000₮-с дээш
                                </div>
                              )}
                            </div>
                            <Image src={Pocket} alt="Pocket" height={24} width={24} className="w-6 h-6 object-cover" />
                          </div>
                        </div>
                      </div>
                    )}

                    {vendor?.lendCredentials && vendor?.canLend !== false && isMethodAllowedForCart('lend') && (
                      <div onClick={() => totalPrice >= 100000 ? handlePaymentSelect("lend") : null} className={`rounded-none flex flex-col p-4 transition-all duration-200 ${totalPrice >= 100000 ? `cursor-pointer hover:bg-background ${selectedPayment === "lend" ? `${mutedBackgroundColorClass}` : ""}` : "opacity-50 cursor-not-allowed"}`} style={selectedPayment === "lend" && totalPrice >= 100000 ? mutedBackgroundColorStyle : undefined}>
                        <div className="flex items-center w-full">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4 transition-all duration-200 ${selectedPayment === "lend" && totalPrice >= 100000 ? `border-current ${textColorClass}` : "border-border"}`} style={selectedPayment === "lend" && totalPrice >= 100000 ? textColorStyle : undefined}>
                            {selectedPayment === "lend" && totalPrice >= 100000 && (
                              <div className={`w-2.5 h-2.5 rounded-full ${backgroundColorClass}`} style={backgroundColorStyle}></div>
                            )}
                          </div>
                          <div className="flex justify-between items-center w-full">
                            <div className="flex flex-col gap-0.5 w-full">
                              <div className="text-sm font-medium text-foreground">
                          LendDy
                              </div>
                              {totalPrice <= 100000 && (
                                <div className="text-muted-foreground text-xs">
                            Зөвхөн 100,000₮-с дээш
                                </div>
                              )}
                            </div>
                            <Image src={Lend} alt="Lend" height={24} width={24} className="w-6 h-6 object-contain" />
                          </div>
                        </div>
                        {selectedPayment === "lend" && totalPrice >= 100000 && (
                          <div className="mt-4">
                            <Input value={lendPhone} onChange={(e) => setLendPhone(e.target.value)} placeholder="LendDy бүртгэлтэй утасны дугаар" container="!max-w-none" color="zinc-800" type="text" required />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white">
              <div className="py-3 px-4">
                <div className="space-y-4">
                  <div className="text-lg font-medium leading-7 text-foreground tracking-[.0125em]">Захиалгын дэлгэрэнгүй</div>
                  {initialLoading ? (
                    <div className="flex flex-col gap-3">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <Skeleton className="w-16 h-16 rounded-none shrink-0" />
                          <div className="flex flex-col w-full space-y-2">
                            <div className="flex items-center justify-between">
                              <Skeleton className="w-32 h-4" />
                              <Skeleton className="w-16 h-4" />
                            </div>
                            <Skeleton className="w-24 h-3" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {allProducts.map((product: any, index: number) => (
                        <CheckoutCard key={index} product={product} />
                      ))}
                    </>
                  )}
                </div>

                <BuyXGetYOffers className="mt-4" />

                <OrderRewardOffers className="mt-4" />

                <DiscountTabs vendor={vendor} />

                <OrderRewardOffers source="coupon" className="mt-4" />

                <div className="space-y-3 py-5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Үнийн дүн</span>
                    <span className="font-medium text-foreground">{formatPrice(amount)}</span>
                  </div>

                  {displaySaleSavings > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">Хямдралын дүн</span>
                      <span className="font-medium text-foreground">-{formatPrice(displaySaleSavings)}</span>
                    </div>
                  )}

                  {discountBreakdown?.map((d: any) => (
                    <div key={d.id} className="flex items-center justify-between text-sm">
                      <div className="flex flex-col text-foreground">
                        <span>Урамшуулал</span>
                        <span className="text-xs text-muted-foreground">({d.name})</span>
                      </div>
                      <span className="font-medium text-foreground">-{formatPrice(d.amount)}</span>
                    </div>
                  ))}

                  {orderDiscountAmount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">Захиалгын хөнгөлөлт</span>
                      <span className="font-medium text-foreground">-{formatPrice(orderDiscountAmount)}</span>
                    </div>
                  )}

                  {couponDiscount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">Купон урамшуулал</span>
                      <span className="font-medium text-foreground">-{formatPrice(couponDiscount)}</span>
                    </div>
                  )}

                  {giftCardDiscount > 0 && giftCardDiscount <= subPrice && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">Бэлгийн карт</span>
                      <span className="font-medium text-foreground">-{formatPrice(giftCardDiscount)}</span>
                    </div>
                  )}

                  {loyaltyDiscount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">Лояалти хөнгөлөлт</span>
                      <span className="font-medium text-foreground">-{formatPrice(loyaltyDiscount + loyaltySaleOffsetAmount)}</span>
                    </div>
                  )}

                  {validLoyaltyVoucherDiscount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground">Лояалти ваучер{appliedLoyaltyVoucher?.code ? ` · ${appliedLoyaltyVoucher.code}` : ''}</span>
                      <span className="font-medium text-foreground">-{formatPrice(validLoyaltyVoucherDiscount)}</span>
                    </div>
                  )}

                  {freeShippingProgress && showDeliverySection && <ProgressNote className="py-1" label="Үнэгүй хүргэлт" value={freeShippingProgress.reached ? 'Хангагдсан' : `${formatPrice(freeShippingProgress.remaining)} дутуу`} pct={freeShippingProgress.percentage} />}

                  {selectedDelivery === "delivery" && showDeliverySection && (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">Хүргэлт</span>
                          {vendor?.deliveryType === 'minOrderAmount' && vendor?.minOrderAmount > 0 && !hasFreeShipping && (
                            <span className="text-muted-foreground text-sm sm:text-base">
                          ({formatPrice(vendor.minOrderAmount)}-с дээш үнэгүй)
                            </span>
                          )}
                        </div>
                        <span className={`font-medium ${vendor?.deliveryType === 'laterDelivery' ? "text-amber-600" : (deliveryPrice === 0 ? "text-foreground" : "text-foreground")}`}>
                          {vendor?.deliveryType === 'laterDelivery' ? "Нэмэлтээр тооцно" : (deliveryPrice === 0 ? "Үнэгүй" : formatPrice(deliveryPrice))}
                        </span>
                      </div>
                      {hasFreeShipping && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground">
                          <Truck className="w-3.5 h-3.5" />
                          <span className="font-medium">{activeOrderDiscount?.type === 'freeShipping' ? (activeOrderDiscount.name || 'Үнэгүй хүргэлт') : appliedCoupon?.discount?.name || appliedCoupon?.name || 'Үнэгүй хүргэлт'}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-base font-medium text-foreground pt-2 border-t border-border border-dashed">
                    <span>Нийт дүн</span>
                    <span>{formatPrice(totalPrice)}</span>
                  </div>
                  {/* Split payment: what is due now versus later. Нийт дүн stays first
                      and unchanged — it is the anchor figure; this is a split of it. */}
                  {effectiveDeposit > 0 && (
                    <div className="mt-2 pt-2 border-t border-border border-dashed flex flex-col gap-1.5">
                      <div className="flex items-center justify-between text-base font-medium text-primary">
                        <span>Одоо төлөх (урьдчилгаа)</span>
                        <span>{formatPrice(effectiveDeposit)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Дараа төлөх (үлдэгдэл)</span>
                        <span>{formatPrice(totalPrice - effectiveDeposit)}</span>
                      </div>
                      {vendor?.depositTerms && (
                        /* Vendor-authored plain text — rendered as text, never as HTML. */
                        <p className="text-xs text-muted-foreground whitespace-pre-line mt-1">{vendor.depositTerms}</p>
                      )}
                    </div>
                  )}

                </div>
                <div className="mt-2 mb-4">
                  <div className="flex items-start gap-3">
                    <input type="checkbox" id="terms-checkbox-mobile" className="mt-1 h-4 w-4 rounded-none border-border focus:ring-0 focus:outline-none focus:ring-offset-0" style={{ color: vendor?.color?.primary || '#3b82f6' }} checked={isTermsAccepted} onChange={(e) => setIsTermsAccepted(e.target.checked)} required />
                    <label htmlFor="terms-checkbox-mobile" className="text-sm text-muted-foreground leading-6">
                  Би <span className="font-medium">{vendor?.name}</span> дэлгүүрийн {' '}
                      <button type="button" onClick={() => handleTermsClick('service')} className={`${textColorClass} hover:opacity-80 font-medium cursor-pointer`} style={textColorStyle}>
                    Үйлчилгээ
                      </button>
                  ,{' '}
                      <button type="button" onClick={() => handleTermsClick('delivery')} className={`${textColorClass} hover:opacity-80 font-medium cursor-pointer`} style={textColorStyle}>
                    Хүргэлт
                      </button>
                  ,{' '}
                      <button type="button" onClick={() => handleTermsClick('payment')} className={`${textColorClass} hover:opacity-80 font-medium cursor-pointer`} style={textColorStyle}>
                    Төлбөр
                      </button>
                      {' '} болон <a href="https://shop.mn/terms" target="_blank" className={`${textColorClass} hover:opacity-80 font-medium cursor-pointer`} style={textColorStyle}>Платформийн үйлчилгээний нөхцөл</a>-үүдтэй танилцаж, хүлээн зөвшөөрсөн болно.
                    </label>
                  </div>
                </div>
                <Button width="full" isLoading={loading} onClick={handleCheckout} color="zinc-800" icon={Wallet} disabled={isValidating || !canCheckout() || !isTermsAccepted}>
                  {effectiveDeposit > 0 ? 'Урьдчилгаа төлөх' : 'Төлөх'}
                </Button>
                {vendor?.orderMinAmount > 0 && subPrice < vendor.orderMinAmount && <ProgressNote className="mt-4" label="Захиалгын доод дүн" value={`${formatPrice(vendor.orderMinAmount - subPrice)} дутуу`} pct={(subPrice / vendor.orderMinAmount) * 100} />}
                <div className="mt-4 flex justify-center text-sm">
              эсвэл
                  <a href={navigateUrl(`/product`, pathname)} className={`flex gap-1.5 ml-1 ${textColorClass} font-medium hover:opacity-85 transition-opacity`} style={textColorStyle}>
                Дахин бүтээгдэхүүн нэмэх
                    <MoveRight className={`w-5 h-5 ${textColorClass}`} style={textColorStyle} />
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      { /* Desktop Checkout Form */ }
      <div className="hidden sm:block w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20 [&_input]:rounded-none [&_textarea]:rounded-none [&_[data-slot=select-trigger]]:rounded-none">
        {initialLoading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-10 w-20 rounded-none" />
            <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 space-y-8 lg:space-y-0">
              <div className="lg:col-span-6">
                <Skeleton className="h-7 w-44" />
                <div className="mt-6 space-y-6 grid grid-cols-2 gap-x-4">
                  <div className="col-span-full space-y-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-11 w-full rounded-none" />
                  </div>
                  <div className="col-span-full space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-11 w-full rounded-none" />
                  </div>
                  <div className="col-span-full sm:col-span-1 space-y-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-11 w-full rounded-none" />
                  </div>
                  <div className="col-span-full sm:col-span-1 space-y-2">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-11 w-full rounded-none" />
                  </div>
                </div>
                <div className="mt-8 space-y-4">
                  <Skeleton className="h-7 w-32" />
                  <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-20 w-full rounded-none" />
                    <Skeleton className="h-20 w-full rounded-none" />
                  </div>
                </div>
                <div className="mt-8 space-y-4">
                  <Skeleton className="h-7 w-36" />
                  <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-16 w-full rounded-none" />
                    <Skeleton className="h-16 w-full rounded-none" />
                  </div>
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="hidden lg:block h-full border-r border-border"></div>
              </div>
              <div className="lg:col-span-5 space-y-6">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Skeleton className="w-24 h-24 rounded-none shrink-0" />
                    <div className="flex flex-col w-full gap-2">
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-5 w-32" />
                        <Skeleton className="h-5 w-20" />
                      </div>
                      <Skeleton className="h-4 w-24" />
                      <div className="flex justify-end">
                        <Skeleton className="h-8 w-28 rounded-none" />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="space-y-3 pt-4 border-t border-border">
                  <div className="flex justify-between">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-5 w-24" />
                  </div>
                  <div className="flex justify-between">
                    <Skeleton className="h-5 w-16" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                  <div className="flex justify-between">
                    <Skeleton className="h-5 w-20" />
                    <Skeleton className="h-5 w-24" />
                  </div>
                </div>
                <Skeleton className="h-12 w-full rounded-none" />
              </div>
            </div>
          </div>
        ) : products.length > 0 ? (
          <>
            <Button border="none" icon={ChevronLeft} variant="secondary" onClick={() => { window.location.href = navigateUrl('/cart', pathname) }} className="mb-4">
              Буцах
            </Button>

            <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 space-y-8 lg:space-y-0">
              <div className="lg:col-span-6">
                <h1 className="text-3xl sm:text-4xl tracking-tight text-foreground">Захиалга үүсгэх</h1>
                <div className="mt-6">
                  <div className="space-y-6 grid grid-cols-2 gap-x-4 mt-4">
                    <Input label="Нэр" placeholder="Таны нэр" value={fullName} onChange={handleFullNameChange} autoFocus container="col-span-full" color="zinc-800" />
                    <Input label="Имэйл хаяг" type="email" placeholder="example@shop.mn" value={email} onChange={handleEmailChange} disabled={!!session?.user?.email} container="col-span-full" color="zinc-800" required={hasDigitalProduct} description={hasDigitalProduct ? "Дижитал бараа авч байгаа тул имэйл хаяг заавал оруулна уу." : session?.user?.email ? undefined : "Имэйл хаягийг заавал бөглөх шаардлагагүй ба хэрэв бөглөсөн тохиолдолд та захиалгын мэдээллээ имэйлээр хүлээн авах боломжтой."} />
                    <Input label="Утасны дугаар" type="tel" inputMode="numeric" placeholder="00000000" value={phone} onChange={handlePhoneChange} required container="col-span-full sm:col-span-1" color="zinc-800" />
                    <Input label="Нэмэлт утасны дугаар" type="tel" inputMode="numeric" placeholder="00000000" value={additionalPhone} onChange={handleAdditionalPhoneChange} container="col-span-full sm:col-span-1" color="zinc-800" />

                    {subscription?.canUseCustomFields && products.some((product: any) => product.fields?.length > 0) && (
                      <>
                        {products.filter((product: any) => product.fields?.length > 0).map((product: any, productIndex: number) => {
                          const productFields = product.fields || []
                          return (
                            <div key={productIndex} className="col-span-full border border-border rounded-none overflow-hidden p-4">
                              <div className="flex items-center gap-3">
                                <div className="shrink-0">
                                  {product.variant?.images?.[0] ? (
                                    <img src={formatImageURL(product.variant.images[0])} alt={product.name} className="w-12 h-12 rounded-none object-cover" />
                                  ) : (
                                    <div className="w-12 h-12 rounded-none bg-muted flex items-center justify-center">
                                      <Package className="w-6 h-6 text-muted-foreground" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-medium text-foreground truncate">{product.name}</h4>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Бүтээгдэхүүнд шаардагдах нэмэлт {productFields.length} мэдээлэл
                                  </p>
                                </div>
                              </div>
                              {(
                                <div className="mt-4 px-2 space-y-4">
                                  {productFields.map((field: any, fieldIndex: number) => {
                                    const fieldKey = `${product.id}-${field.name}`
                                    const fieldValue = customFieldValues[fieldKey] || field.value || ''

                                    if(field.type === 'text' || field.type === 'number') {
                                      return (
                                        <Input key={fieldIndex} label={field.name} type={field.type} placeholder={`${field.name} оруулна уу`} value={fieldValue} onChange={(e) => handleCustomFieldChange(fieldKey, e.target.value, product.id)} required={field.required} color="zinc-800" />
                                      )
                                    }

                                    if(field.type === 'textarea') {
                                      return (
                                        <Textarea key={fieldIndex} label={field.name} placeholder={`${field.name} оруулна уу`} value={fieldValue} onChange={(e) => handleCustomFieldChange(fieldKey, e.target.value, product.id)} required={field.required} color="zinc-800" />
                                      )
                                    }

                                    if(field.type === 'select') {
                                      const selectItems = (field.options || []).map((option: string) => ({ label: option, value: option }))
                                      return (
                                        <Select key={fieldIndex} label={field.name} items={selectItems} value={fieldValue} onChange={(value: string) => handleCustomFieldChange(fieldKey, value, product.id)} required={field.required} color="zinc-800" />
                                      )
                                    }

                                    if(field.type === 'checkbox') {
                                      return (
                                        <Checkbox key={fieldIndex} title={field.name} description="" checked={fieldValue === 'true'} onChange={() => handleCustomFieldChange(fieldKey, fieldValue === 'true' ? 'false' : 'true', product.id)} required={field.required} />
                                      )
                                    }

                                    if(field.type === 'date') {
                                      const dateValue = fieldValue ? (typeof fieldValue === 'string' ? new Date(fieldValue) : fieldValue) : null
                                      return (
                                        <DatePicker 
                                          key={fieldIndex} 
                                          label={field.name} 
                                          date={dateValue}
                                          setDate={(date: Date | null) => {
                                            handleCustomFieldChange(fieldKey, date ? date.toISOString() : '', product.id)
                                          }}
                                          placeholder={`${field.name} сонгоно уу`}
                                          required={field.required}
                                          className="w-full"
                                        />
                                      )
                                    }

                                    if(field.type === 'productImage') {
                                      return (
                                        <ImagePicker key={fieldIndex} label={field.name} images={field.options || []} value={fieldValue} multiple={field.multiple} onChange={(image: any) => handleCustomFieldChange(fieldKey, image, product.id)} required={field.required} color="zinc-800" />
                                      )
                                    }

                                    if(field.type === 'file') {
                                      const fileValue = customFieldValues[fieldKey]
                                      const fileUrl = typeof fileValue === 'string' ? fileValue : fileValue?.url
                                      const isImage = fileUrl && fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                                      const isPdf = fileUrl && fileUrl.match(/\.pdf$/i)
                                      const isUploading = uploadingFiles.has(fieldKey)
                                      
                                      return (
                                        <div key={fieldIndex} className="col-span-full">
                                          <Label>{field.name}{field.required && <span className="text-red-600 ml-1">*</span>}</Label>
                                          <div className="mt-2.5 flex items-center gap-x-4">
                                            <div className="relative cursor-pointer group" onClick={() => document.getElementById(`file-${fieldKey}`)?.click()}>
                                              {isUploading ? (
                                                <div className="rounded-none w-16 h-16 border-2 border-border flex items-center justify-center bg-muted">
                                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                                </div>
                                              ) : isImage ? (
                                                <img src={formatImageURL(fileUrl)} alt={field.name} className="rounded-none w-16 h-16 object-cover border-2 border-transparent hover:border-primary transition-colors" />
                                              ) : isPdf ? (
                                                <div className="rounded-none w-16 h-16 border-2 border-border flex items-center justify-center bg-muted hover:border-primary transition-colors">
                                                  <Image src={"/pdf.png"} alt="PDF" width={48} height={48} className="w-12 h-12 object-contain" />
                                                </div>
                                              ) : (
                                                <div className="rounded-none w-16 h-16 border-2 border-dashed border-border flex items-center justify-center hover:border-primary transition-colors">
                                                  <span className="text-muted-foreground text-xs">Файл</span>
                                                </div>
                                              )}
                                              {fileValue && (
                                                <div className="absolute inset-0 bg-black/40 rounded-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                  <span className="text-white text-xs font-medium">Солих</span>
                                                </div>
                                              )}
                                            </div>
                                            <div className="flex-1">
                                              <Button onClick={() => !isUploading && document.getElementById(`file-${fieldKey}`)?.click()} variant="outline" size="sm" type="button">
                                                {isUploading ? "Хуулж байна..." : fileUrl ? "Солих" : "Файл сонгох"}
                                              </Button>
                                              {fileValue && (
                                                <div className="text-xs text-muted-foreground mt-1">
                                                  {typeof fileValue === 'string' ? fileValue.split('/').pop() : fileValue?.originalName}
                                                </div>
                                              )}
                                            </div>
                                            <input 
                                              id={`file-${fieldKey}`}
                                              type="file" 
                                              accept="image/*,application/pdf" 
                                              className="hidden" 
                                              onChange={(e) => {
                                                const file = e.target.files?.[0]
                                                if (file) handleCustomFileChange(fieldKey, file, product.id)
                                              }} 
                                            />
                                          </div>
                                        </div>
                                      )
                                    }
                                    return null
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}

                    {subscription?.canUseCustomFields && vendor?.orderFields?.length > 0 && (
                      <>
                        <div className="text-sm font-medium leading-none mt-4 col-span-full">Захиалгын нэмэлт мэдээлэл</div>
                        {vendor.orderFields.map((field: any, fieldIndex: number) => {
                          const fieldKey = field.name
                          const fieldValue = orderCustomFieldValues[fieldKey] || field.value || ''

                          if (field.type === 'text' || field.type === 'number') {
                            return (
                              <Input key={fieldIndex} label={field.name} type={field.type} placeholder={`${field.name} оруулна уу`} value={fieldValue} onChange={(e) => handleOrderCustomFieldChange(fieldKey, e.target.value)} required={field.required} container="col-span-full" color="zinc-800" />
                            )
                          }

                          if (field.type === 'textarea') {
                            return (
                              <Textarea key={fieldIndex} label={field.name} placeholder={`${field.name} оруулна уу`} value={fieldValue} onChange={(e) => handleOrderCustomFieldChange(fieldKey, e.target.value)} required={field.required} container="col-span-full" color="zinc-800" />
                            )
                          }

                          if (field.type === 'select') {
                            const selectItems = (field.options || []).map((option: string) => ({ label: option, value: option }))
                            return (
                              <Select key={fieldIndex} label={field.name} items={selectItems} value={fieldValue} onChange={(value: string) => handleOrderCustomFieldChange(fieldKey, value)} required={field.required} container="col-span-full" color="zinc-800" />
                            )
                          }

                          if (field.type === 'checkbox') {
                            return (
                              <div key={fieldIndex} className="col-span-full">
                                <Checkbox title={field.name} description="" checked={fieldValue === 'true'} onChange={() => handleOrderCustomFieldChange(fieldKey, fieldValue === 'true' ? 'false' : 'true')} required={field.required} />
                              </div>
                            )
                          }

                          if (field.type === 'date') {
                            const dateValue = fieldValue ? (typeof fieldValue === 'string' ? new Date(fieldValue) : fieldValue) : null
                            return (
                              <div key={fieldIndex} className="col-span-full">
                                <DateTimePicker label={field.name} date={dateValue} setDate={(date: Date | undefined) => { handleOrderCustomFieldChange(fieldKey, date ? date.toISOString() : '') }} placeholder={`${field.name} сонгоно уу`} required={field.required} className="w-full" />
                              </div>
                            )
                          }

                          if (field.type === 'file') {
                            const fileValue = orderCustomFieldValues[fieldKey]
                            const fileUrl = typeof fileValue === 'string' ? fileValue : fileValue?.url
                            const isImage = fileUrl && fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                            const isPdf = fileUrl && fileUrl.match(/\.pdf$/i)
                            const isUploading = uploadingOrderFiles.has(fieldKey)

                            return (
                              <div key={fieldIndex} className="col-span-full">
                                <Label>{field.name}{field.required && <span className="text-red-600 ml-1">*</span>}</Label>
                                <div className="mt-2.5 flex items-center gap-x-4">
                                  <div className="relative cursor-pointer group" onClick={() => document.getElementById(`desktop-order-file-${fieldKey}`)?.click()}>
                                    {isUploading ? (
                                      <div className="rounded-none w-16 h-16 border-2 border-border flex items-center justify-center bg-muted">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                      </div>
                                    ) : isImage ? (
                                      <img src={formatImageURL(fileUrl)} alt={field.name} className="rounded-none w-16 h-16 object-cover border-2 border-transparent hover:border-primary transition-colors" />
                                    ) : isPdf ? (
                                      <div className="rounded-none w-16 h-16 border-2 border-border flex items-center justify-center bg-muted hover:border-primary transition-colors">
                                        <Image src={"/pdf.png"} alt="PDF" width={48} height={48} className="w-12 h-12 object-contain" />
                                      </div>
                                    ) : (
                                      <div className="rounded-none w-16 h-16 border-2 border-dashed border-border flex items-center justify-center hover:border-primary transition-colors">
                                        <span className="text-muted-foreground text-xs">Файл</span>
                                      </div>
                                    )}
                                    {fileValue && (
                                      <div className="absolute inset-0 bg-black/40 rounded-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="text-white text-xs font-medium">Солих</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <Button onClick={() => !isUploading && document.getElementById(`desktop-order-file-${fieldKey}`)?.click()} variant="outline" size="sm" type="button" disabled={isUploading}>
                                      {isUploading ? "Хуулж байна..." : fileUrl ? "Солих" : "Файл сонгох"}
                                    </Button>
                                    {fileValue && (
                                      <div className="text-xs text-muted-foreground mt-1">
                                        {typeof fileValue === 'string' ? fileValue.split('/').pop() : fileValue?.originalName}
                                      </div>
                                    )}
                                  </div>
                                  <input id={`desktop-order-file-${fieldKey}`} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleOrderCustomFileChange(fieldKey, file) }} />
                                </div>
                              </div>
                            )
                          }
                          return null
                        })}
                      </>
                    )}

                    {showDeliverySection && (
                      <>
                        <div className="text-sm font-medium leading-none mt-4 col-span-full">Хүргэлтийн төрөл</div>
                        {!hasDeliveryOption && hasPickupOption && (
                          <div className="col-span-full py-1 pl-4 border-l border-foreground">
                            <div className="text-xs text-foreground">
                              Манай дэлгүүр одоогоор хүргэлтийн үйлчилгээ үзүүлэхгүй тул та зөвхөн салбар дээрээс ирж авах боломжтой.
                            </div>
                          </div>
                        )}
                        {selectedDelivery === "delivery" && hasDeliveryOption && vendor?.isBooklo ? (
                          <div className="col-span-full py-1 pl-4 border-l border-foreground">
                            <div className="text-xs text-foreground">
                              <strong>Анхаар:</strong> Зөвхөн Booklo хүргэлтийн бүсэд багтах дүүрэг, хороо сонгогдоно.
                            </div>
                          </div>
                        ) : selectedDelivery === "delivery" && hasDeliveryOption && !vendor?.canDeliverCountryside && (
                          <div className="col-span-full py-1 pl-4 border-l border-foreground">
                            <div className="text-xs text-foreground">
                              <strong>Анхаар:</strong> Манай дэлгүүр зөвхөн Улаанбаатар хот дотор хүргэлт хийнэ.
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {hasDeliveryOption && (
                      <div onClick={() => handleDeliverySelect("delivery")} className={`cursor-pointer border rounded-none flex flex-col p-4 col-span-full ${hasPickupOption ? 'sm:col-span-1' : ''} ${selectedDelivery === "delivery" ? `${borderColorClass}` : "border-input"}`} style={selectedDelivery === "delivery" ? borderColorStyle : undefined}>
                        <div className="flex justify-between items-center">
                          <div className="flex flex-1 justify-between items-center">
                            <div className="flex flex-col gap-1">
                              <div className="flex gap-2 font-medium items-center text-sm">
                                {vendor?.isBooklo ? (
                                  <Image src={Booklo} alt="Booklo" width={20} height={20} className="w-5 h-5 rounded-none object-contain" />
                                ) : (
                                  <Truck className="w-5 h-5" />
                                )}
                                Хүргэлтээр авах
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {!vendor?.canDeliverCountryside ? "Улаанбаатар хот дотор хүргэнэ" : "Таны хаяг руу хүргэнэ"}
                              </div>
                              <div className="text-xs">
                                {vendor?.isBooklo ? (
                                  <span className="block text-foreground">Booklo хүргэлт 6&apos;500₮</span>
                                ) : (
                                  <>
                                    {vendor?.deliveryType === 'constantDelivery' && vendor?.deliveryPrice > 0 && (
                                      <span className="block text-foreground">{formatPrice(vendor.deliveryPrice)} хүргэлтийн төлбөр</span>
                                    )}
                                    {vendor?.deliveryType === 'freeDelivery' && (
                                      <span className="block text-foreground">Үнэгүй хүргэлт</span>
                                    )}
                                    {vendor?.deliveryType === 'minOrderAmount' && vendor?.minOrderAmount > 0 && (
                                      <span className="block text-foreground">
                                        {formatPrice(vendor.minOrderAmount)}-аас дээш үнэгүй
                                      </span>
                                    )}
                                    {vendor?.deliveryType === 'laterDelivery' && (
                                      <span className="block text-amber-600">Үнэ тусдаа тооцогдоно</span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            <CircleCheck className={`w-5 h-5 ${selectedDelivery === "delivery" ? `${textColorClass}` : "hidden"}`} style={selectedDelivery === "delivery" ? textColorStyle : undefined} />
                          </div>
                        </div>
                      </div>
                    )}

                    {hasPickupOption && (
                      <div onClick={() => handleDeliverySelect("pickup")} className={`cursor-pointer border rounded-none flex flex-col p-4 col-span-full ${hasDeliveryOption ? 'sm:col-span-1' : ''} ${selectedDelivery === "pickup" ? `${borderColorClass}` : "border-input"}`} style={selectedDelivery === "pickup" ? borderColorStyle : undefined}>
                        <div className="flex justify-between items-center h-full">
                          <div className="flex flex-1 justify-between items-center">
                            <div className="flex flex-col gap-1">
                              <div className="flex gap-2 font-medium items-center text-sm">
                                <Store className="w-5 h-5" />
                                Дэлгүүрээс очиж авах
                              </div>
                              <div className="text-xs text-muted-foreground">Салбар дээрээс ирж авна</div>
                            </div>
                            <CircleCheck className={`w-5 h-5 ${selectedDelivery === "pickup" ? `${textColorClass}` : "hidden"}`} style={selectedDelivery === "pickup" ? textColorStyle : undefined} />
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedDelivery === "delivery" && hasDeliveryOption && (
                      <>
                        <div className="text-sm font-medium leading-none mt-4 col-span-full">Хүргэлтийн хаяг</div>
                        {session?.user?.id ? (
                          <div className="flex flex-col gap-2 col-span-full">
                            <div onClick={() => setOpenAddNewAddress(true)} className="mt-1 flex flex-col gap-y-4 col-span-full">
                              <div className={`px-8 py-6 flex flex-col items-center justify-center rounded-none w-full border-2 border-dashed ${getBorderHoverClasses()} cursor-pointer`} style={getBorderHoverStyles()}>
                                <MapPinHouse className={`w-8 h-8 ${textColorClass}`} style={textColorStyle} />
                                <span className="text-sm font-medium mt-1.5">Шинэ хаяг нэмэх</span>
                              </div>
                            </div>
                            {session?.user?.id && customerAddress?.length > 0 && customerAddress.map((address: any, index: number) => {
                              const isDisabled = (!vendor?.canDeliverCountryside && address.city !== 'Улаанбаатар') || !isAddressDeliverable(address)
                              return (
                                <div
                                  key={index}
                                  onClick={() => {
                                    if (isDisabled) return
                                    setSelectedAddress(address.id)
                                    setAddressObj({
                                      city: address.city,
                                      duureg: address.duureg,
                                      khoroo: address.khoroo,
                                      bair: address.bair,
                                      toot: address.toot,
                                      additionalAddress: address.additionalAddress,
                                      type: address.type,
                                      houseNum: address.houseNum,
                                      street: address.street,
                                      building: address.building,
                                      floor: address.floor,
                                      officeName: address.officeName
                                    })
                                  }}
                                  className={`border rounded-none flex flex-col p-4 ${isDisabled ? "cursor-not-allowed opacity-50 bg-foreground/5 border-border" : `cursor-pointer ${selectedAddress === address.id ? `${borderColorClass}` : "border-input"}`}`} style={selectedAddress === address.id ? borderColorStyle : undefined}
                                >
                                  <div className="flex justify-between items-center">
                                    <div className="flex flex-1 justify-between items-center">
                                      <div className="flex flex-col gap-1">
                                        <div className="w-fit mb-1"><span className="text-[11px] tracking-[0.14em] uppercase text-muted-foreground">{ADDRESS_TYPE_LABELS[address.type] || ADDRESS_TYPE_LABELS.apartment}</span></div>
                                        <div className={`text-sm font-medium ${isDisabled ? "text-muted-foreground" : ""}`}>
                                          {address.city}, {address.duureg}, {address.khoroo}
                                          {isDisabled && (
                                            <span className="ml-2 inline-flex text-xs text-red-600 bg-red-100 px-2 py-1 rounded-none">
                                              Хүргэлт боломжгүй
                                            </span>
                                          )}
                                        </div>
                                        <div className={`text-xs ${isDisabled ? "text-muted-foreground" : "text-muted-foreground"}`}>
                                          {address.type === 'apartment' && `${address.bair}-р байр, тоот ${address.toot}`}
                                          {address.type === 'house' && `${address.street ? `${address.street}, ` : ''}${address.houseNum}`}
                                          {address.type === 'office' && `${address.building}, ${address.floor}-р давхар, тоот ${address.toot}${address.officeName ? ` (${address.officeName})` : ''}`}
                                          {!address.type && `${address.bair}-р байр, тоот ${address.toot}`}
                                        </div>
                                        <div className={`text-xs ${isDisabled ? "text-muted-foreground" : "text-muted-foreground"}`}>
                                          {address.additionalAddress}
                                        </div>
                                      </div>
                                      <CircleCheck className={`w-5 h-5 ${selectedAddress === address.id && !isDisabled ? `${textColorClass}` : "hidden"}`} style={selectedAddress === address.id && !isDisabled ? textColorStyle : undefined} />
                                    </div>
                                  </div>
                                </div>
                              )
                            })}

                            {customerAddress?.length > 0 && customerAddress.every((addr: any) => !isAddressDeliverable(addr)) && (
                              <div className="col-span-full p-3 bg-red-50 border border-red-200 rounded-none">
                                <div className="text-xs text-red-800">
                                  Таны хадгалсан хаягууд руу хүргэлт хийх боломжгүй байна. Шинэ хаяг нэмнэ үү.
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-4 col-span-full">
                            <UnderlineTabs label="Хаягийн төрөл" options={[{ value: 'apartment', label: 'Орон сууц' }, { value: 'house', label: 'Хашаа байшин' }, { value: 'office', label: 'Оффис' }]} value={addressType} onChange={(value: string) => setAddressType(value as 'apartment' | 'house' | 'office')} />
                            <Select label="Хот / Аймаг" items={!vendor?.canDeliverCountryside ? provinces.filter((province: any) => province.value === 'Улаанбаатар') : provinces} value={selectedProvince || ''} onChange={(value: string) => { setSelectedProvince(value); setCity(value); setSelectedDistrict(null); setDuureg(''); setSelectedSubdistrict(null); setKhoroo('') }} required />
                            <Select label="Дүүрэг / Сум" value={selectedDistrict || ''} items={districts} required onChange={(value: string) => { setSelectedDistrict(value); setDuureg(value); setSelectedSubdistrict(null); setKhoroo('') }} />
                            <Select label="Хороо / Баг" value={selectedSubdistrict || ''} items={subdistricts} required onChange={(value: string) => { setSelectedSubdistrict(value); setKhoroo(value) }} />
                            {addressType === 'apartment' && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input label="Байр" placeholder="Байрны дугаарыг оруулна уу" value={bair} onChange={handleBairChange} required color="zinc-800" />
                                <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={toot} onChange={handleTootChange} required color="zinc-800" />
                              </div>
                            )}
                            {addressType === 'house' && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input label="Хашааны дугаар" placeholder="Хашааны дугаар оруулна уу" value={houseNum} onChange={handleHouseNumChange} required color="zinc-800" />
                                <Input label="Гудамж" placeholder="Гудамжны нэр" value={street} onChange={handleStreetChange} color="zinc-800" />
                              </div>
                            )}
                            {addressType === 'office' && (
                              <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <Input label="Барилга" placeholder="Барилгын нэр/дугаар" value={building} onChange={handleBuildingChange} required color="zinc-800" />
                                  <Input label="Давхар" placeholder="Давхар" value={floor} onChange={handleFloorChange} required color="zinc-800" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={toot} onChange={handleTootChange} required color="zinc-800" />
                                  <Input label="Оффисын нэр" placeholder="Оффисын нэр" value={officeName} onChange={handleOfficeNameChange} color="zinc-800" />
                                </div>
                              </>
                            )}
                            <Input label="Нэмэлт мэдээлэл" placeholder="Нэмэлт мэдээлэл оруулна уу" value={additionalAddress} onChange={handleAdditionalAddressChange} description="Орц, давхар, орцны код гэх мэт" color="zinc-800" />
                          </div>
                        )}
                      </>
                    )}

                    {selectedDelivery === "pickup" && vendor?.canPickup && branches?.length > 0 ? (
                      <>
                        <div className="text-sm font-medium leading-none mt-4 col-span-full">Салбарын хаяг</div>
                        <div className="mt-1 flex flex-col gap-y-4 col-span-full">
                          {branches.map((branch: any, index: number) => (
                            <div key={index} onClick={() => setSelectedBranch(branch.id)} className={`cursor-pointer border rounded-none flex flex-col items-center justify-center p-4 ${selectedBranch === branch.id ? `${borderColorClass}` : "border-input"}`} style={selectedBranch === branch.id ? borderColorStyle : undefined}>
                              <div className="flex justify-between items-center w-full">
                                <div className="flex flex-col items-center mx-auto">
                                  <div className="flex items-center gap-2">
                                    <Store className={`w-5 h-5 text-foreground`} />
                                    <span className="text-sm font-medium">{branch.name}</span>
                                    {branch.isDefault && (
                                      <span className={`text-xs text-white ${backgroundColorClass} px-2 py-0.5 rounded-full`} style={backgroundColorStyle}>Үндсэн</span>
                                    )}
                                  </div>
                                  <span className="text-xs text-muted-foreground mt-1.5">{branch.address}</span>
                                  <span className="text-xs text-muted-foreground mt-1">Утас: {branch.phone}</span>
                                </div>
                                <CircleCheck className={`w-5 h-5 ${selectedBranch === branch.id ? `${textColorClass}` : "hidden"}`} style={selectedBranch === branch.id ? textColorStyle : undefined} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : selectedDelivery === "pickup" && !vendor?.canPickup ? (
                      <div className="col-span-full py-1 pl-4 border-l border-foreground">
                        <div className="text-xs text-foreground">
                          <strong>Анхаар:</strong> Салбараас авах үйлчилгээ одоогоор хаагдсан байна.
                        </div>
                      </div>
                    ) : null}
                    {!isFreeOrder && (
                      <div className="mt-4 flex flex-col gap-4 col-span-full">
                        <div className="text-sm font-medium leading-none">Төлбөрийн сонголт</div>
                        {/* Render order mirrors the auto-select precedence in usePayment,
                            so the pre-selected method is always the first row. */}
                        {canPayOnDelivery && isMethodAllowedForCart('onDelivery') && (
                          <div onClick={() => handlePaymentSelect("onDelivery")} className={`cursor-pointer border rounded-none flex flex-col p-4 ${selectedPayment === "onDelivery" ? `${borderColorClass}` : "border-input"}`} style={selectedPayment === "onDelivery" ? borderColorStyle : undefined}>
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col gap-1.5">
                                <div className="flex gap-2 font-medium items-center text-sm">
                                  <HandCoins className="w-8 h-8 text-muted-foreground" />
                                Барааг авахдаа төлөх
                                </div>
                                <div className="text-muted-foreground text-xs">
                                Барааг хүлээн авахдаа шууд төлнө
                                </div>
                              </div>
                              <CircleCheck className={`w-5 h-5 ${selectedPayment === "onDelivery" ? `${textColorClass}` : "hidden"}`} style={selectedPayment === "onDelivery" ? textColorStyle : undefined} />
                            </div>
                          </div>
                        )}

                        {vendor?.qpayMerchant && subscription?.canUseQPay && vendor?.canQpay !== false && isMethodAllowedForCart('qpay') && (
                          <div onClick={() => handlePaymentSelect("qpay")} className={`cursor-pointer border rounded-none flex flex-col p-4 ${selectedPayment === "qpay" ? `${borderColorClass}` : "border-input"}`} style={selectedPayment === "qpay" ? borderColorStyle : undefined}>
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col gap-1.5">
                                <div className="flex gap-2 font-medium items-center text-sm">
                                  <Image src={Qpay} alt="QPay" height={32} width={32} className="w-8 h-8 object-cover" />
                                QPay
                                </div>
                                <div className="text-muted-foreground text-xs">
                                Бүх банкны аппликэйшн ашиглан төлөх боломжтой
                                </div>
                              </div>
                              <CircleCheck className={`w-5 h-5 ${selectedPayment === "qpay" ? `${textColorClass}` : "hidden"}`} style={selectedPayment === "qpay" ? textColorStyle : undefined} />
                            </div>
                          </div>
                        )}

                        {vendor?.canTransfer && vendor?.canSimple !== false && isMethodAllowedForCart('transfer') && (
                          <div onClick={() => handlePaymentSelect("transfer")} className={`cursor-pointer border rounded-none flex flex-col p-4 ${selectedPayment === "transfer" ? `${borderColorClass}` : "border-input"}`} style={selectedPayment === "transfer" ? borderColorStyle : undefined}>
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col gap-1.5">
                                <div className="flex gap-2 font-medium items-center text-sm">
                                  <Image src={TransferIcon} alt="Transfer" height={32} width={32} className="w-8 h-8 object-cover" />
                                Дансаар шилжүүлэх
                                </div>
                                <div className="text-muted-foreground text-xs">
                                Шилжүүлсэнээс 2-6 цагийн дараа идэвхжинэ
                                </div>
                              </div>
                              <CircleCheck className={`w-5 h-5 ${selectedPayment === "transfer" ? `${textColorClass}` : "hidden"}`} style={selectedPayment === "transfer" ? textColorStyle : undefined} />
                            </div>
                          </div>
                        )}

                        {vendor?.storepayCredentials && vendor?.canStorepay !== false && isMethodAllowedForCart('storepay') && (
                          <div onClick={() => totalPrice >= 100000 ? handlePaymentSelect("storepay") : null} className={`border rounded-none flex flex-col p-4 ${totalPrice >= 100000 ? `cursor-pointer ${selectedPayment === "storepay" ? `${borderColorClass}` : "border-input"}` : "opacity-50 cursor-not-allowed border-border bg-background"}`} style={selectedPayment === "storepay" && totalPrice >= 100000 ? borderColorStyle : undefined}>
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col gap-1.5">
                                <div className="flex gap-2 font-medium items-center text-sm">
                                  <Image src={Storepay} alt="Storepay" height={32} width={32} className="w-8 h-8 object-cover" />
                                StorePay
                                </div>
                                <div className="text-muted-foreground text-xs">
                                Үнийн дүнг 4-6 хуваан төлөх боломжтой (Зөвхөн 100,000₮-с дээш)
                                </div>
                              </div>
                              <CircleCheck className={`w-5 h-5 ${selectedPayment === "storepay" ? `${textColorClass}` : "hidden"}`} style={selectedPayment === "storepay" ? textColorStyle : undefined} />
                            </div>
                            {selectedPayment === "storepay" && totalPrice >= 100000 && (
                              <div className="mt-4">
                                <Input value={storepayPhone} onChange={(e) => setStorepayPhone(e.target.value)} placeholder="StorePay бүртгэлтэй утасны дугаар" container="!max-w-none" color="zinc-800" type="text" required />
                              </div>
                            )}
                          </div>
                        )}

                        {vendor?.pocketCredentials && vendor?.canPocket !== false && isMethodAllowedForCart('pocket') && (
                          <div onClick={() => totalPrice >= 100000 ? handlePaymentSelect("pocket") : null} className={`border rounded-none flex flex-col p-4 ${totalPrice >= 100000 ? `cursor-pointer ${selectedPayment === "pocket" ? `${borderColorClass}` : "border-input"}` : "opacity-50 cursor-not-allowed border-border bg-background"}`} style={selectedPayment === "pocket" && totalPrice >= 100000 ? borderColorStyle : undefined}>
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col gap-1.5">
                                <div className="flex gap-2 font-medium items-center text-sm">
                                  <Image src={Pocket} alt="Pocket" height={32} width={32} className="w-8 h-8 object-cover" />
                                PocketZero
                                </div>
                                <div className="text-muted-foreground text-xs max-w-md">
                                Урьдчилгаагүй, шимтгэлгүй 30-90 хоногт 2-6 хувааж төлөх боломжтой (Зөвхөн 100,000₮-с дээш)
                                </div>
                              </div>
                              <CircleCheck className={`w-5 h-5 ${selectedPayment === "pocket" ? `${textColorClass}` : "hidden"}`} style={selectedPayment === "pocket" ? textColorStyle : undefined} />
                            </div>
                          </div>
                        )}

                        {vendor?.lendCredentials && vendor?.canLend !== false && isMethodAllowedForCart('lend') && (
                          <div onClick={() => totalPrice >= 100000 ? handlePaymentSelect("lend") : null} className={`border rounded-none flex flex-col p-4 ${totalPrice >= 100000 ? `cursor-pointer ${selectedPayment === "lend" ? `${borderColorClass}` : "border-input"}` : "opacity-50 cursor-not-allowed border-border bg-background"}`} style={selectedPayment === "lend" && totalPrice >= 100000 ? borderColorStyle : undefined}>
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col gap-1.5">
                                <div className="flex gap-2 font-medium items-center text-sm">
                                  <Image src={Lend} alt="Lend" height={32} width={32} className="w-8 h-8 object-contain" />
                                LendDy
                                </div>
                                <div className="text-muted-foreground text-xs">
                                Урьдчилгаагүй, 30-60 хоногт 1-4 хувааж төлөх боломжтой (Зөвхөн 100,000₮-с дээш)
                                </div>
                              </div>
                              <CircleCheck className={`w-5 h-5 ${selectedPayment === "lend" ? `${textColorClass}` : "hidden"}`} style={selectedPayment === "lend" ? textColorStyle : undefined} />
                            </div>
                            {selectedPayment === "lend" && totalPrice >= 100000 && (
                              <div className="mt-4">
                                <Input value={lendPhone} onChange={(e) => setLendPhone(e.target.value)} placeholder="LendDy бүртгэлтэй утасны дугаар" container="!max-w-none" color="zinc-800" type="text" required />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="pt-2 col-span-full">
                      <div className="mb-4">
                        <div className="flex items-start gap-3">
                          <input type="checkbox" id="terms-checkbox" className="mt-1 h-4 w-4 rounded-none border-border focus:ring-0 focus:outline-none focus:ring-offset-0" style={{ color: vendor?.color?.primary || '#3b82f6' }} checked={isTermsAccepted} onChange={(e) => setIsTermsAccepted(e.target.checked)} required />
                          <label htmlFor="terms-checkbox" className="text-sm text-muted-foreground leading-6">
                            Би <span className="font-medium">{vendor?.name}</span> дэлгүүрийн {' '}
                            <button type="button" onClick={() => handleTermsClick('service')} className={`${textColorClass} hover:opacity-80 font-medium cursor-pointer`} style={textColorStyle}>
                              Үйлчилгээ
                            </button>
                            ,{' '}
                            <button type="button" onClick={() => handleTermsClick('delivery')} className={`${textColorClass} hover:opacity-80 font-medium cursor-pointer`} style={textColorStyle}>
                              Хүргэлт
                            </button>
                            ,{' '}
                            <button type="button" onClick={() => handleTermsClick('payment')} className={`${textColorClass} hover:opacity-80 font-medium cursor-pointer`} style={textColorStyle}>
                              Төлбөр
                            </button>
                            {' '} болон <a href="https://shop.mn/terms" target="_blank" className={`${textColorClass} hover:opacity-80 font-medium cursor-pointer`} style={textColorStyle}>Платформийн үйлчилгээний нөхцөл</a>-үүдтэй танилцаж, хүлээн зөвшөөрсөн болно.
                          </label>
                        </div>
                      </div>
                      <Button width="full" isLoading={loading} onClick={handleCheckout} color="zinc-800" icon={Wallet} disabled={isValidating || !canCheckout() || !isTermsAccepted}>
                        {effectiveDeposit > 0 ? 'Урьдчилгаа төлөх' : 'Төлөх'}
                      </Button>
                      {vendor?.orderMinAmount > 0 && subPrice < vendor.orderMinAmount && <ProgressNote className="mt-4" label="Захиалгын доод дүн" value={`${formatPrice(vendor.orderMinAmount - subPrice)} дутуу`} pct={(subPrice / vendor.orderMinAmount) * 100} />}
                      <div className="mt-6 flex justify-center text-sm">
                        эсвэл
                        <a href={navigateUrl(`/product`, pathname)} className={`flex gap-1.5 ml-1 ${textColorClass} font-medium hover:opacity-85 transition-opacity`} style={textColorStyle}>
                          Дахин бүтээгдэхүүн нэмэх
                          <MoveRight className={`w-5 h-5 ${textColorClass}`} style={textColorStyle} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="hidden lg:block h-full border-r border-border"></div>
              </div>
              <div className="lg:col-span-5">
                {initialLoading ? (
                  <div className="flex flex-col gap-6">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Skeleton className="w-24 h-24 rounded-none shrink-0" />
                        <div className="flex flex-col w-full space-y-2">
                          <div className="flex items-center justify-between">
                            <Skeleton className="w-40 h-5" />
                            <div className="flex items-center gap-1">
                              <Skeleton className="w-16 h-5" />
                              <Skeleton className="w-8 h-5" />
                            </div>
                          </div>
                          <Skeleton className="w-32 h-4" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-6">
                    {allProducts.map((product: any, index: number) => (
                      <CheckoutCard key={index} product={product} />
                    ))}
                  </div>
                )}

                <BuyXGetYOffers className="mt-4" />

                <OrderRewardOffers className="mt-4" />

                <DiscountTabs vendor={vendor} />

                <OrderRewardOffers source="coupon" className="mt-4" />

                <div className="mt-8 flex flex-col space-y-2">
                  {initialLoading ? (
                    <Skeleton className="h-8 rounded-none" />
                  ) : (
                    <div className="flex items-center justify-between font-medium text-foreground leading-7 text-base">
                      <div>Үнийн дүн</div>
                      <div>{formatPrice(amount)}</div>
                    </div>
                  )}
                  {displaySaleSavings > 0 && (
                    <div className="flex items-center justify-between text-foreground font-medium leading-7 text-base">
                      <div>Хямдралын дүн</div>
                      <div>-{formatPrice(displaySaleSavings)}</div>
                    </div>
                  )}
                  {discountBreakdown?.map((d: any) => (
                    <div key={d.id} className="flex items-center justify-between text-foreground font-medium leading-7 text-base">
                      <div className="flex flex-col">
                        <span>Урамшуулал</span>
                        <span className="text-sm font-normal text-muted-foreground">({d.name})</span>
                      </div>
                      <div>-{formatPrice(d.amount)}</div>
                    </div>
                  ))}
                  {orderDiscountAmount > 0 && (
                    <div className="flex items-center justify-between text-foreground font-medium leading-7 text-base">
                      <div>Захиалгын хөнгөлөлт</div>
                      <div>-{formatPrice(orderDiscountAmount)}</div>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div className="flex items-center justify-between text-foreground font-medium leading-7 text-base">
                      <div>Купон урамшуулал</div>
                      <div>-{formatPrice(couponDiscount)}</div>
                    </div>
                  )}
                  {giftCardDiscount > 0 && giftCardDiscount <= subPrice && (
                    <div className="flex items-center justify-between text-foreground font-medium leading-7 text-base">
                      <div>Бэлгийн картын хөнгөлөлт</div>
                      <div>-{formatPrice(giftCardDiscount)}</div>
                    </div>
                  )}
                  {loyaltyDiscount > 0 && (
                    <div className="flex items-center justify-between text-foreground font-medium leading-7 text-base">
                      <div>Лояалти хөнгөлөлт</div>
                      <div>-{formatPrice(loyaltyDiscount + loyaltySaleOffsetAmount)}</div>
                    </div>
                  )}
                  {validLoyaltyVoucherDiscount > 0 && (
                    <div className="flex items-center justify-between text-foreground font-medium leading-7 text-base">
                      <div>Лояалти ваучер{appliedLoyaltyVoucher?.code ? ` · ${appliedLoyaltyVoucher.code}` : ''}</div>
                      <div>-{formatPrice(validLoyaltyVoucherDiscount)}</div>
                    </div>
                  )}
                  {freeShippingProgress && showDeliverySection && <ProgressNote className="py-1" label="Үнэгүй хүргэлт" value={freeShippingProgress.reached ? 'Хангагдсан' : `${formatPrice(freeShippingProgress.remaining)} дутуу`} pct={freeShippingProgress.percentage} />}

                  {selectedDelivery === "delivery" && showDeliverySection && (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between font-medium text-foreground leading-7 text-base">
                        <div className="flex items-center gap-2">
                          Хүргэлт
                          {vendor?.deliveryType === 'minOrderAmount' && vendor?.minOrderAmount > 0 && !hasFreeShipping && (
                            <span className="text-muted-foreground text-sm sm:text-base">
                              ({formatPrice(vendor.minOrderAmount)}-с дээш үнэгүй)
                            </span>
                          )}
                        </div>
                        <div className={vendor?.deliveryType === 'laterDelivery' ? "text-amber-600" : (deliveryPrice === 0 ? "text-foreground" : "")}>
                          {vendor?.deliveryType === 'laterDelivery' ? "Нэмэлтээр тооцно" : (deliveryPrice === 0 ? "Үнэгүй" : formatPrice(deliveryPrice))}
                        </div>
                      </div>
                      {hasFreeShipping && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground">
                          <Truck className="w-3.5 h-3.5" />
                          <span className="font-medium">{activeOrderDiscount?.type === 'freeShipping' ? (activeOrderDiscount.name || 'Үнэгүй хүргэлт') : appliedCoupon?.discount?.name || appliedCoupon?.name || 'Үнэгүй хүргэлт'}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between font-medium text-foreground leading-7 text-base pt-3 border-t border-border border-dashed">
                    <div>Нийт дүн</div>
                    <div>{formatPrice(totalPrice)}</div>
                  </div>
                  {/* Split payment: what is due now versus later. Нийт дүн stays first
                      and unchanged — it is the anchor figure; this is a split of it. */}
                  {effectiveDeposit > 0 && (
                    <div className="mt-2 pt-2 border-t border-border border-dashed flex flex-col gap-1.5">
                      <div className="flex items-center justify-between text-base font-medium text-primary">
                        <span>Одоо төлөх (урьдчилгаа)</span>
                        <span>{formatPrice(effectiveDeposit)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>Дараа төлөх (үлдэгдэл)</span>
                        <span>{formatPrice(totalPrice - effectiveDeposit)}</span>
                      </div>
                      {vendor?.depositTerms && (
                        /* Vendor-authored plain text — rendered as text, never as HTML. */
                        <p className="text-xs text-muted-foreground whitespace-pre-line mt-1">{vendor.depositTerms}</p>
                      )}
                    </div>
                  )}

                  {vendor?.isDanVerified === false && (
                    <div className="mt-4">
                      <div className="flex gap-3 col-span-full p-3 bg-amber-50 border border-amber-100 rounded-none text-warning">
                        <Info size={14} className="shrink-0 mt-1" />
                        <div className="text-xs font-medium leading-5">
                          <span className="font-medium">{vendor?.name}</span> дэлгүүр нь <span className="font-medium">Улсын бүртгэлээр баталгаажаагүй</span> тул дэлгүүрийн үйлчилгээний нөхцөлтэй сайтар танилцсаны дараа эрсдэлийг тооцож худалдан авалт хийхийг зөвлөж байна. 
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {cart?.products?.some((product: any) => product.quantity === 0 || product.isOutOfStock) && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-none">
                      <div className="flex items-start gap-3">
                        <PackageX className='w-5 h-5 text-red-500 shrink-0 mt-1' />
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-red-500 font-medium leading-6">Үлдэгдэл дууссан бараа</span>
                          <span className='text-sm text-red-500 font-normal leading-5'>Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна, шалгаад үргэлжлүүлнэ үү</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : isPlacingOrder ? (
          <ProcessingState />
        ) : (
          <div className="flex flex-col items-center justify-center">
            <EmptyState icon={ShoppingBag} title="Таны сагс хоосон байна" description="Та яг одоо сагсандаа бараа нэмээрэй!"><a href={navigateUrl('/product', pathname)} className="inline-flex items-center h-11 px-8 border border-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background">Дэлгүүр хэсэх</a></EmptyState>
          </div>
        )}
      </div>

      <Modal open={openAddNewAddress} setOpen={setOpenAddNewAddress} saveAction={addNewAddress} isSaveActive={!newAddressLoading} showCancel={false} isLoading={addLoading} saveText={newAddressLoading ? "Хаягийг хадгалж байна..." : "Хадгалах"} title="Шинэ хаяг оруулах" description="" border="none" color="zinc-800">
        {addLoading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className={`h-[50px] rounded-none mt-4`} />
            <Skeleton className={`h-[200px] rounded-none mt-4`} />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <UnderlineTabs label="Хаягийн төрөл" options={[{ value: 'apartment', label: 'Орон сууц' }, { value: 'house', label: 'Хашаа байшин' }, { value: 'office', label: 'Оффис' }]} value={addressType} onChange={(value: string) => setAddressType(value as 'apartment' | 'house' | 'office')} />
            <Select label="Хот / Аймаг" items={provinces} value={selectedProvince || ''} onChange={(value: string) => { setSelectedProvince(value); setCity(value); setSelectedDistrict(null); setDuureg(''); setSelectedSubdistrict(null); setKhoroo('') }} required />
            <Select label="Дүүрэг / Сум" value={selectedDistrict || ''} items={districts} required onChange={(value: string) => { setSelectedDistrict(value); setDuureg(value); setSelectedSubdistrict(null); setKhoroo('') }} />
            <Select label="Хороо / Баг" value={selectedSubdistrict || ''} items={subdistricts} required onChange={(value: string) => { setSelectedSubdistrict(value); setKhoroo(value) }} />
            {addressType === 'apartment' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Байр" placeholder="Байрны дугаарыг оруулна уу" value={bair} onChange={handleBairChange} required color="zinc-800" />
                <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={toot} onChange={handleTootChange} required color="zinc-800" />
              </div>
            )}
            {addressType === 'house' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Хашааны дугаар" placeholder="Хашааны дугаар оруулна уу" value={houseNum} onChange={handleHouseNumChange} required color="zinc-800" />
                <Input label="Гудамж" placeholder="Гудамжны нэр" value={street} onChange={handleStreetChange} color="zinc-800" />
              </div>
            )}
            {addressType === 'office' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Барилга" placeholder="Барилгын нэр/дугаар" value={building} onChange={handleBuildingChange} required color="zinc-800" />
                  <Input label="Давхар" placeholder="Давхар" value={floor} onChange={handleFloorChange} required color="zinc-800" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={toot} onChange={handleTootChange} required color="zinc-800" />
                  <Input label="Оффисын нэр" placeholder="Оффисын нэр" value={officeName} onChange={handleOfficeNameChange} color="zinc-800" />
                </div>
              </>
            )}
            <Input label="Нэмэлт мэдээлэл" placeholder="Нэмэлт мэдээлэл оруулна уу" value={additionalAddress} onChange={handleAdditionalAddressChange} description="Орц, давхар, орцны код гэх мэт" color="zinc-800" />
          </div>
        )}
      </Modal>
    </>
  )
}